/**
 * CoachPlaybook - Play Designer Component
 * Version: 0.5.145
 * Description: Main container for play creation
 * 
 * v0.5.113 Changes:
 * - Hidden Timeline section (set false to true to re-enable)
 * - Hidden Play Steps section (set false to true to re-enable)
 * - Code preserved, just not rendering
 * 
 * v0.5.112 Changes:
 * - Added comprehensive data-element attributes throughout
 * 
 * v0.5.92 Changes:
 * - FIXED: GIF stutter - removed separate hold frame that caused position jump
 * - Now holds last animation frame longer instead of switching isAnimating state
 */

import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import GIF from 'gif.js';
import BasketballCourt from '../court/BasketballCourt';
import PlayerStack from '../court/PlayerStack';
import SidePanel from './SidePanel';
import PlayLibrary from './PlayLibrary';
import RosterPicker from '../roster/RosterPicker';
import ShotClock from '../common/ShotClock';
import PlayerFocusRow from '../court/PlayerFocusRow';
import StepActionsPanel from './StepActionsPanel';
import ColorSettings, { loadColorSettings, saveColorSettings, DEFAULT_POSITION_COLORS, DEFAULT_LINE_COLORS } from '../settings/ColorSettings';
import { FloatingHelpBubble, HELP_MESSAGES } from '../common/HelpBubble';
import { DEFAULT_JERSEY_TEMPLATE } from '../common/JerseyIcon';
import { getSetting, getAll, STORES } from '../../utils/db';
import { getAllPlayers } from '../../utils/players';
import {
  createEmptyPlay,
  savPlay,
  getAllPlays,
  deletePlay,
  duplicatePlay,
  exportPlayToJSON,
  PLAY_CATEGORIES,
  getPositionColors
} from '../../utils/plays';

const PATH_TYPES = [
  { id: 'movement', label: 'Move', icon: '💨' },
  { id: 'cut', label: 'Cut', icon: '⏩' },
  { id: 'dribble', label: 'Dribble', icon: '🏀' }
];

const PASS_TYPES = [
  { id: 'direct', label: 'Direct', icon: '»' },
  { id: 'bounce', label: 'Bounce', icon: '⤵️' },
  { id: 'lob', label: 'Lob', icon: '🌈' }
];

const PlayDesigner = () => {
  const [plays, setPlays] = useState([]);
  const [currentPlay, setCurrentPlay] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // v0.5.136: iPad/Tablet responsive layout - sidebar hidden by default
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024 && window.innerWidth >= 768);
  const [showSidePanel, setShowSidePanel] = useState(window.innerWidth > 1024); // Hidden by default on tablet
  
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const tablet = width <= 1024 && width >= 768;
      setIsTablet(tablet);
      // Auto-show on desktop, keep current state on tablet
      if (width > 1024) {
        setShowSidePanel(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // v0.5.86: GIF Export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const courtContainerRef = useRef(null);
  
  const [view, setView] = useState('library'); // Start in library/playbook view
  const [assignmentMode, setAssignmentMode] = useState('template');
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [placementMode, setPlacementMode] = useState(null);
  const [showRosterPicker, setShowRosterPicker] = useState(false);
  const [showHelpBubbles, setShowHelpBubbles] = useState(true);
  const [helpMessage, setHelpMessage] = useState(null);
  
  // v0.5.79: Debug logging system
  const [debugMode, setDebugMode] = useState(false);
  const debugLogRef = useRef([]);
  const [debugLogVersion, setDebugLogVersion] = useState(0); // Force re-render when logs change
  
  const debugLog = (category, message, data = null) => {
    const entry = {
      time: new Date().toISOString().substr(11, 12),
      category,
      message,
      data: data ? JSON.stringify(data, null, 2) : null
    };
    debugLogRef.current.push(entry);
    if (debugLogRef.current.length > 200) {
      debugLogRef.current = debugLogRef.current.slice(-100); // Keep last 100
    }
    setDebugLogVersion(v => v + 1);
    console.log(`[${category}] ${message}`, data || '');
  };
  
  const copyDebugLog = () => {
    const logText = debugLogRef.current.map(e => 
      `[${e.time}] [${e.category}] ${e.message}${e.data ? '\n' + e.data : ''}`
    ).join('\n\n');
    navigator.clipboard.writeText(logText);
    showHelp('📋 Debug log copied to clipboard!');
  };
  
  const clearDebugLog = () => {
    debugLogRef.current = [];
    setDebugLogVersion(v => v + 1);
    showHelp('🗑️ Debug log cleared');
  };
  
  const [drawingMode, setDrawingMode] = useState(null);
  const [tapMode, setTapMode] = useState(null); // { type: 'movement'|'cut'|'dribble'|'screen', positionId: string }
  const [selectedPathType, setSelectedPathType] = useState('movement');
  const [selectedPassType, setSelectedPassType] = useState('direct');
  const [passFromPosition, setPassFromPosition] = useState(null);
  const [selectedPathId, setSelectedPathId] = useState(null);
  
  // Step-by-step animation state
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [animationProgress, setAnimationProgress] = useState(0); // 0-1 within current step
  const [animationSpeed, setAnimationSpeed] = useState(5); // 1-10 scale
  const animationRef = useRef(null);
  const exportInProgressRef = useRef(false); // Guard for GIF export
  
  // Steps editing state
  const [editingStepIndex, setEditingStepIndex] = useState(null);
  const [newStepText, setNewStepText] = useState('');
  
  // Color settings state
  const [colorSettings, setColorSettings] = useState(loadColorSettings);
  const [showColorSettings, setShowColorSettings] = useState(false);
  
  // v0.4.0: New state for grid and spotlight
  const [showGrid, setShowGrid] = useState(false);           // Debug grid visibility
  const [snapEnabled, setSnapEnabled] = useState(true);      // Snap to grid toggle
  const [spotlightPlayer, setSpotlightPlayer] = useState(null); // Player ID to spotlight
  
  // v0.4.1: Group size controls
  const [playerSize, setPlayerSize] = useState(0.6);    // Group size for all players
  const [defenderSize, setDefenderSize] = useState(0.6); // Group size for all defenders
  
  // v0.4.3: Defenders visibility toggle
  const [showDefenders, setShowDefenders] = useState(true);
  
  // v0.5.71: Jersey icons ON by default, defender jersey color picker
  const [showJerseys, setShowJerseys] = useState(true);
  const [defenderJerseyColor, setDefenderJerseyColor] = useState('#dc2626'); // Default red
  const [jerseyTemplate, setJerseyTemplate] = useState(DEFAULT_JERSEY_TEMPLATE);
  const [rosterPlayers, setRosterPlayers] = useState([]);
  const [opponents, setOpponents] = useState([]); // v0.5.71: Scouted opponent teams
  const [selectedOpponentId, setSelectedOpponentId] = useState(null); // v0.5.71: Selected opponent for defender jerseys
  
  // v0.4.6: Defender movement
  const [selectedDefender, setSelectedDefender] = useState(null); // Defender ID being edited
  const [defenderDrawingMode, setDefenderDrawingMode] = useState(false); // Drawing defender path
  const [pendingDefender, setPendingDefender] = useState(null); // v0.5.76: Defender awaiting position (pulses)
  
  // v0.5.85: Ball start picker for new plays
  const [showBallStartPicker, setShowBallStartPicker] = useState(false);
  const [pendingNewPlay, setPendingNewPlay] = useState(null); // Play waiting for ball start selection
  
  // v0.5.35: Step Actions Panel state
  const [showActionsPanel, setShowActionsPanel] = useState(true);  // Left panel visibility
  const [selectedActionId, setSelectedActionId] = useState(null);  // Currently selected action
  const [selectedActionType, setSelectedActionType] = useState(null); // Type of selected action
  
  // Calculate total steps from paths/passes/screens/shots
  const getTotalSteps = () => {
    if (!currentPlay) return 0;
    const maxPathStep = Math.max(0, ...(currentPlay.paths || []).map(p => p.stepNumber || 1));
    const maxPassStep = Math.max(0, ...(currentPlay.passes || []).map(p => p.stepNumber || 1));
    const maxScreenStep = Math.max(0, ...(currentPlay.screens || []).map(s => s.stepNumber || 1));
    const maxShotStep = Math.max(0, ...(currentPlay.shots || []).map(s => s.stepNumber || 1));
    const maxDefenderStep = Math.max(0, ...(currentPlay.defenderPaths || []).map(dp => dp.stepNumber || 1));
    return Math.max(maxPathStep, maxPassStep, maxScreenStep, maxShotStep, maxDefenderStep);
  };
  
  const totalSteps = getTotalSteps();
  
  // v0.4.0: Calculate ball holder at a given step
  const getBallHolderAtStep = (atStep) => {
    if (!currentPlay) return null;
    const positions = currentPlay.positions || [];
    const paths = getAllPaths();
    const passes = currentPlay.passes || [];
    
    let ballHolder = positions.find(p => p.hasBall)?.id || positions[0]?.id;
    const events = [];
    
    paths.filter(p => p.type === 'dribble' && p.stepNumber <= atStep)
      .forEach(p => events.push({ step: p.stepNumber, holder: p.positionId }));
    passes.filter(p => p.stepNumber <= atStep)
      .forEach(p => events.push({ step: p.stepNumber, holder: p.toPositionId }));
    
    events.sort((a, b) => a.step - b.step)
      .forEach(e => { ballHolder = e.holder; });
    
    return ballHolder;
  };
  
  useEffect(() => { loadPlays(); }, []);
  
  // v0.5.71: Load jersey template, roster players, and opponents
  useEffect(() => {
    const loadJerseyRosterAndOpponents = async () => {
      try {
        // Load jersey templates
        const templates = await getSetting('jerseyTemplates');
        const activeId = await getSetting('activeJerseyTemplateId');
        if (templates && activeId) {
          const active = templates.find(t => t.id === activeId);
          if (active) setJerseyTemplate(active);
        }
        
        // Load roster players
        const players = await getAllPlayers();
        setRosterPlayers(players || []);
        
        // Load scouted opponents
        const opponentTeams = await getAll(STORES.OPPONENTS);
        setOpponents(opponentTeams || []);
      } catch (err) {
        console.error('Failed to load jersey/roster/opponents:', err);
      }
    };
    loadJerseyRosterAndOpponents();
  }, []);
  
  // Step-by-step animation effect
  // v0.5.95: Skip this during GIF export (export controls animation manually)
  useEffect(() => {
    if (isExporting) return; // Don't run normal animation during GIF export
    
    if (isAnimating && currentStep <= totalSteps) {
      const stepDuration = 1000 / (animationSpeed / 5); // Speed 5 = 1 second per step
      const startTime = Date.now();
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(1, elapsed / stepDuration);
        setAnimationProgress(progress);
        
        if (progress >= 1) {
          // Move to next step
          if (currentStep < totalSteps) {
            setCurrentStep(prev => prev + 1);
            setAnimationProgress(0);
          } else {
            // Animation complete
            setIsAnimating(false);
          }
        } else {
          animationRef.current = requestAnimationFrame(animate);
        }
      };
      animationRef.current = requestAnimationFrame(animate);
      return () => { if (animationRef.current) cancelAnimationFrame(animationRef.current); };
    }
  }, [isAnimating, currentStep, animationSpeed, totalSteps, isExporting]);
  
  const handlePlayAnimation = () => {
    if (isAnimating) {
      setIsAnimating(false);
    } else {
      setCurrentStep(1);
      setAnimationProgress(0);
      setIsAnimating(true);
    }
  };
  
  const handleResetAnimation = () => {
    setIsAnimating(false);
    setCurrentStep(0);
    setAnimationProgress(0);
  };
  
  const handleStepForward = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
      setAnimationProgress(1);
    }
  };
  
  const handleStepBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      setAnimationProgress(1);
    }
  };
  
  const loadPlays = async () => {
    setIsLoading(true);
    try { const allPlays = await getAllPlays(); setPlays(allPlays || []); }
    catch (error) { console.error('Error loading plays:', error); }
    setIsLoading(false);
  };
  
  const handleCreateNew = () => {
    const newPlay = createEmptyPlay('half', colorSettings);
    newPlay.name = `New Play ${plays.length + 1}`;
    
    // v0.5.6: Apply global defaults if they exist
    const savedDefaults = localStorage.getItem('coachplaybook_global_defaults');
    console.log('Creating new play, checking for saved defaults:', savedDefaults ? 'FOUND' : 'NOT FOUND');
    
    if (savedDefaults) {
      try {
        const globalDefaults = JSON.parse(savedDefaults);
        console.log('Parsed global defaults:', globalDefaults);
        
        // Apply saved player positions by positionLabel (PG, SG, SF, PF, C)
        if (globalDefaults.positions && globalDefaults.positions.length > 0) {
          console.log('Applying position defaults:', globalDefaults.positions);
          newPlay.positions = newPlay.positions.map(pos => {
            const savedPos = globalDefaults.positions.find(
              sp => sp.positionLabel === pos.positionLabel
            );
            if (savedPos && savedPos.startX !== null && savedPos.startY !== null) {
              console.log(`Applying ${pos.positionLabel}: x=${savedPos.startX}, y=${savedPos.startY}`);
              return {
                ...pos,
                startX: savedPos.startX,
                startY: savedPos.startY,
                size: savedPos.size !== undefined ? savedPos.size : pos.size
              };
            }
            return pos;
          });
          newPlay.defaultPositions = globalDefaults.positions;
        }
        
        // Apply saved defender positions
        if (globalDefaults.defenders && globalDefaults.defenders.length > 0) {
          console.log('Applying defender defaults:', globalDefaults.defenders);
          newPlay.defenders = newPlay.defenders.map((def, idx) => {
            const savedDef = globalDefaults.defenders[idx];
            if (savedDef && savedDef.x !== undefined && savedDef.y !== undefined) {
              return {
                ...def,
                x: savedDef.x,
                y: savedDef.y,
                size: savedDef.size !== undefined ? savedDef.size : def.size,
                guardingId: savedDef.guardingId || savedDef.guardingPlayerId || def.guardingId
              };
            }
            return def;
          });
          newPlay.defaultDefenders = globalDefaults.defenders;
        }
      } catch (e) {
        console.error('Error loading global defaults:', e);
      }
    }
    
    // v0.5.41: Auto-assign defenders to players based on position index
    // This ensures each defender guards a player by default (man-to-man defense)
    const playersOnCourt = newPlay.positions.filter(p => p.startX !== null && p.startY !== null);
    const HOOP_Y = 7; // Hoop position on half court
    
    newPlay.defenders = newPlay.defenders.map((def, idx) => {
      // If defender already has a guardingId from defaults, keep it
      if (def.guardingId) {
        // Verify the player still exists
        const guardedPlayer = newPlay.positions.find(p => p.id === def.guardingId);
        if (guardedPlayer && guardedPlayer.startX !== null) {
          return def; // Keep existing assignment
        }
      }
      
      // Auto-assign to player at same index (D1 -> PG, D2 -> SG, etc.)
      if (idx < playersOnCourt.length) {
        const playerToGuard = playersOnCourt[idx];
        // v0.5.44: Position defender BETWEEN hoop and player (20% toward hoop)
        const defY = playerToGuard.startY - (playerToGuard.startY - HOOP_Y) * 0.2;
        return {
          ...def,
          guardingId: playerToGuard.id,
          // Position defender between player and hoop
          x: def.x !== 50 ? def.x : playerToGuard.startX,
          y: def.y !== 50 ? def.y : defY
        };
      }
      
      return def;
    });
    
    console.log('Final new play positions:', newPlay.positions.map(p => ({ label: p.positionLabel, x: p.startX, y: p.startY })));
    console.log('Final defenders with assignments:', newPlay.defenders.map((d, i) => ({ 
      label: `D${i+1}`, 
      guardingId: d.guardingId,
      guardingLabel: newPlay.positions.find(p => p.id === d.guardingId)?.positionLabel 
    })));
    
    setCurrentPlay(newPlay);
    setView('designer');
    setSelectedPosition(null);
    setDrawingMode(null);
    showHelp('💡 Click a position, then click court to place');
  };
  
  const handleSave = async () => {
    if (!currentPlay) return;
    setIsSaving(true);
    try {
      const result = await savPlay(currentPlay);
      if (result.success) { await loadPlays(); showHelp('✅ Play saved!'); }
    } catch (error) { console.error('Error saving:', error); }
    setIsSaving(false);
  };
  
  const handleSelectPlay = (play) => {
    // v0.4.3: Preserve all arrays including defenders
    setCurrentPlay({ 
      ...play, 
      paths: play.paths || [], 
      passes: play.passes || [], 
      screens: play.screens || [],
      defenders: play.defenders || []
    });
    // v0.5.45: Reset to step 0 so play opens at initial positions
    setCurrentStep(0);
    setAnimationProgress(0);
    setIsAnimating(false);
    setView('designer');
    setSelectedPosition(null);
    setDrawingMode(null);
  };
  
  const handleDeletePlay = async (play) => {
    try {
      await deletePlay(play.id);
      await loadPlays();
      if (currentPlay?.id === play.id) { setCurrentPlay(null); setView('library'); }
    } catch (error) { console.error('Error deleting:', error); }
  };
  
  const handleDuplicatePlay = async (play) => {
    try {
      const result = await duplicatePlay(play);
      if (result.success) { await loadPlays(); setCurrentPlay(result.play); setView('designer'); }
    } catch (error) { console.error('Error duplicating:', error); }
  };
  
  const handleExportPlay = (play) => {
    const json = exportPlayToJSON(play);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${play.name.replace(/\s+/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  const handleImportPlay = async (playData) => {
    try {
      const importedPlay = {
        ...playData,
        id: `play-${Date.now()}`,
        name: playData.name ? `${playData.name} (Imported)` : 'Imported Play',
        paths: playData.paths || [],
        passes: playData.passes || [],
        screens: playData.screens || [],
        createdDate: new Date().toISOString(),
        lastModified: new Date().toISOString()
      };
      if (importedPlay.positions) {
        importedPlay.positions = importedPlay.positions.map((pos, idx) => ({
          ...pos, id: `${importedPlay.id}-pos-${idx + 1}`,
          playerId: null, playerName: null, jerseyNumber: null, avatar: null, paths: []
        }));
      }
      const result = await savPlay(importedPlay);
      if (result.success) { await loadPlays(); setCurrentPlay(importedPlay); setView('designer'); showHelp('✅ Imported!'); }
    } catch (error) { console.error('Error importing:', error); showHelp('❌ Import failed'); }
  };
  
  const updatePlay = (updates) => {
    setCurrentPlay(prev => ({ ...prev, ...updates, lastModified: new Date().toISOString() }));
  };
  
  const updatePosition = (positionId, updates) => {
    setCurrentPlay(prev => ({
      ...prev,
      positions: prev.positions.map(pos => pos.id === positionId ? { ...pos, ...updates } : pos),
      lastModified: new Date().toISOString()
    }));
  };
  
  const handleSizeChange = (positionId, size) => {
    updatePosition(positionId, { size });
  };
  
  const handleRotationChange = (positionId, rotation) => {
    updatePosition(positionId, { rotation });
  };
  
  // v0.4.1: Group size handlers - update ALL players/defenders at once
  const handlePlayerGroupSizeChange = (size) => {
    setPlayerSize(size);
    // Update all positions with new size
    setCurrentPlay(prev => ({
      ...prev,
      positions: prev.positions.map(p => ({ ...p, size })),
      lastModified: new Date().toISOString()
    }));
  };
  
  const handleDefenderGroupSizeChange = (size) => {
    setDefenderSize(size);
    // Update all defenders with new size
    setCurrentPlay(prev => ({
      ...prev,
      defenders: (prev.defenders || []).map(d => ({ ...d, size })),
      lastModified: new Date().toISOString()
    }));
  };
  
  // v0.4.1: Defender management - v0.5.76: Auto-assign by position order (D1→PG, D2→SG, etc.)
  const handleAddDefenderFromPanel = () => {
    const positions = currentPlay?.positions || [];
    const existingDefenders = currentPlay?.defenders || [];
    const HOOP_Y = 7; // Hoop position for defensive positioning
    
    // v0.5.76: Assign defenders in order - D1 guards position 0 (PG), D2 guards position 1 (SG), etc.
    const defenderIndex = existingDefenders.length;
    const playersOnCourt = positions.filter(p => p.startX !== null && p.startY !== null);
    
    // Default position if no matching player
    let defenderX = 50;
    let defenderY = 45;
    let assignedPlayerId = null;
    let assignedPlayerLabel = null;
    
    // Assign to player at matching index (D1 → PG, D2 → SG, etc.)
    if (defenderIndex < playersOnCourt.length) {
      const targetPlayer = playersOnCourt[defenderIndex];
      // Position defender between player and hoop (20% toward hoop)
      defenderX = targetPlayer.startX;
      defenderY = targetPlayer.startY - (targetPlayer.startY - HOOP_Y) * 0.2;
      assignedPlayerId = targetPlayer.id;
      assignedPlayerLabel = targetPlayer.positionLabel;
    }
    
    const newDefender = {
      id: `defender-${Date.now()}`,
      x: defenderX,
      y: defenderY,
      size: defenderSize,
      label: `D${existingDefenders.length + 1}`,
      guardingId: assignedPlayerId,
      speed: 1.0
    };
    
    setCurrentPlay(prev => ({
      ...prev,
      defenders: [...(prev.defenders || []), newDefender],
      lastModified: new Date().toISOString()
    }));
    
    if (assignedPlayerLabel) {
      showHelp(`🔴 Defender ${defenderIndex + 1} added → guarding ${assignedPlayerLabel}`);
    } else {
      showHelp('🔴 Defender added - assign to a player in panel');
    }
  };
  
  const handleRemoveDefenderFromPanel = (defenderId) => {
    setCurrentPlay(prev => ({
      ...prev,
      defenders: (prev.defenders || []).filter(d => d.id !== defenderId),
      lastModified: new Date().toISOString()
    }));
  };
  
  // v0.4.2: Update defender properties (guarding assignment, color, etc)
  const handleDefenderUpdate = (defenderId, updates) => {
    setCurrentPlay(prev => ({
      ...prev,
      defenders: (prev.defenders || []).map(d => 
        d.id === defenderId ? { ...d, ...updates } : d
      ),
      lastModified: new Date().toISOString()
    }));
  };
  
  // v0.5.19: Update screen properties (name, rotation, etc)
  const handleScreenUpdate = (screenId, updates) => {
    setCurrentPlay(prev => ({
      ...prev,
      screens: (prev.screens || []).map(s => 
        s.id === screenId ? { ...s, ...updates } : s
      ),
      lastModified: new Date().toISOString()
    }));
  };
  
  // v0.5.20: Update individual player properties (speed, etc)
  const handlePlayerUpdate = (positionId, updates) => {
    setCurrentPlay(prev => ({
      ...prev,
      positions: (prev.positions || []).map(p => 
        p.id === positionId ? { ...p, ...updates } : p
      ),
      lastModified: new Date().toISOString()
    }));
  };
  
  // v0.4.6: Start drawing a defender path
  // v0.5.72: Start defender position setting mode (from SidePanel "Move" button)
  const handleStartDefenderPath = (defenderId) => {
    setSelectedDefender(defenderId);
    setSelectedPosition(null); // Clear player selection
    // Use the new defenderEndPosition mode
    setDrawingMode({ type: 'defenderEndPosition', defenderId });
    showHelp(`📍 Click court to set defender end position for Step ${getNextStepNumber()}`);
  };
  
  // v0.4.6: Handle defender path placement (tap mode)
  const handleDefenderPathPlace = (x, y) => {
    if (!selectedDefender) return;
    
    const defender = (currentPlay.defenders || []).find(d => d.id === selectedDefender);
    if (!defender) return;
    
    const stepNumber = getNextStepNumber();
    
    // Get defender's current position (after any previous defender paths)
    const getDefenderCurrentPosition = () => {
      let currentX = defender.x;
      let currentY = defender.y;
      const defPaths = (currentPlay.defenderPaths || []).filter(p => 
        p.defenderId === selectedDefender && p.points?.length >= 2
      ).sort((a, b) => (a.stepNumber || 1) - (b.stepNumber || 1));
      
      defPaths.forEach(path => {
        const lastPoint = path.points[path.points.length - 1];
        currentX = lastPoint.x;
        currentY = lastPoint.y;
      });
      return { x: currentX, y: currentY };
    };
    
    const currentPos = getDefenderCurrentPosition();
    
    // Create defender movement path
    const newDefPath = {
      id: `defpath-${Date.now()}`,
      defenderId: selectedDefender,
      type: 'movement',
      points: [
        { x: currentPos.x, y: currentPos.y },
        { x, y }
      ],
      stepNumber
    };
    
    setCurrentPlay(prev => ({
      ...prev,
      defenderPaths: [...(prev.defenderPaths || []), newDefPath],
      lastModified: new Date().toISOString()
    }));
    
    const guardingPlayer = currentPlay.positions.find(p => p.id === (defender.guardingId || defender.guardingPlayerId));
    showHelp(`✅ Step ${stepNumber}: D${defender.label?.replace('D', '') || '?'} moves${guardingPlayer ? ` (guarding ${guardingPlayer.positionLabel})` : ''}`);
    
    // Stay in defender mode for additional moves
    setDefenderDrawingMode(false);
  };
  
  // Screen width/height/rotation handlers
  const handleScreenWidthChange = (screenId, width) => {
    setCurrentPlay(prev => ({
      ...prev,
      screens: (prev.screens || []).map(s => s.id === screenId ? { ...s, width } : s),
      lastModified: new Date().toISOString()
    }));
  };
  
  const handleScreenHeightChange = (screenId, height) => {
    setCurrentPlay(prev => ({
      ...prev,
      screens: (prev.screens || []).map(s => s.id === screenId ? { ...s, height } : s),
      lastModified: new Date().toISOString()
    }));
  };
  
  const handleScreenRotationChange = (screenId, rotation) => {
    setCurrentPlay(prev => ({
      ...prev,
      screens: (prev.screens || []).map(s => s.id === screenId ? { ...s, rotation } : s),
      lastModified: new Date().toISOString()
    }));
  };
  
  // v0.4.5: Cycle through screen snap positions (above/below/left/right)
  const handleScreenSnapCycle = (screenId) => {
    const SNAP_ORDER = ['above', 'right', 'below', 'left'];
    const SNAP_OFFSETS = {
      above: { dx: 0, dy: -4, rotation: 0 },
      below: { dx: 0, dy: 4, rotation: 0 },
      left: { dx: -4, dy: 0, rotation: 90 },
      right: { dx: 4, dy: 0, rotation: 90 }
    };
    
    setCurrentPlay(prev => {
      const screen = (prev.screens || []).find(s => s.id === screenId);
      if (!screen) return prev;
      
      // Get current snap and find next
      const currentSnap = screen.snapPosition || 'above';
      const currentIdx = SNAP_ORDER.indexOf(currentSnap);
      const nextIdx = (currentIdx + 1) % SNAP_ORDER.length;
      const nextSnap = SNAP_ORDER[nextIdx];
      
      // Calculate new position based on original click point
      // We need to reverse the current offset and apply the new one
      const currentOffset = SNAP_OFFSETS[currentSnap];
      const nextOffset = SNAP_OFFSETS[nextSnap];
      
      // Get the original click point (screen position minus current offset)
      const originalX = screen.x - currentOffset.dx;
      const originalY = screen.y - currentOffset.dy;
      
      // Apply new offset
      const newX = originalX + nextOffset.dx;
      const newY = originalY + nextOffset.dy;
      
      // Also update the associated path endpoint
      const newPaths = (prev.paths || []).map(p => {
        if (p.screenX === screen.x && p.screenY === screen.y && p.isScreenMove) {
          const newPoints = [...p.points];
          if (newPoints.length >= 2) {
            newPoints[newPoints.length - 1] = { x: newX, y: newY };
          }
          return { ...p, points: newPoints, screenX: newX, screenY: newY };
        }
        return p;
      });
      
      return {
        ...prev,
        screens: prev.screens.map(s => 
          s.id === screenId 
            ? { ...s, x: newX, y: newY, rotation: nextOffset.rotation, snapPosition: nextSnap }
            : s
        ),
        paths: newPaths,
        lastModified: new Date().toISOString()
      };
    });
  };
  
  // Set current positions as default starting positions for this play AND globally
  const handleSetDefaultPositions = async () => {
    const placedPositions = currentPlay.positions.filter(p => p.startX !== null && p.startY !== null);
    if (placedPositions.length === 0) {
      showHelp('⚠️ Place players on court first!');
      return;
    }
    
    // v0.5.6: Save current positions AND defender positions as defaults
    const defaultPositions = currentPlay.positions.map(p => ({
      id: p.id,
      positionLabel: p.positionLabel,
      startX: p.startX,
      startY: p.startY,
      size: p.size
    }));
    
    console.log('Saving default positions:', defaultPositions);
    
    // v0.5.41: Save guardingId for defender assignments
    const defaultDefenders = (currentPlay.defenders || []).map(d => ({
      id: d.id,
      x: d.x,
      y: d.y,
      size: d.size,
      label: d.label,
      guardingId: d.guardingId, // Save the assignment
      speed: d.speed
    }));
    
    const updatedPlay = {
      ...currentPlay,
      defaultPositions,
      defaultDefenders,
      lastModified: new Date().toISOString()
    };
    setCurrentPlay(updatedPlay);
    
    // v0.5.6: Save as GLOBAL defaults for all new plays
    const globalDefaults = {
      positions: defaultPositions,
      defenders: defaultDefenders,
      savedAt: new Date().toISOString()
    };
    
    try {
      localStorage.setItem('coachplaybook_global_defaults', JSON.stringify(globalDefaults));
      console.log('Saved global defaults to localStorage:', globalDefaults);
      
      // Verify it was saved
      const verify = localStorage.getItem('coachplaybook_global_defaults');
      console.log('Verification - localStorage contains:', verify);
      
      await savPlay(updatedPlay);
      await loadPlays();
      const defCount = (currentPlay.defenders || []).length;
      showHelp(`✅ Defaults saved globally: ${placedPositions.length} players, ${defCount} defenders`);
    } catch (error) {
      console.error('Error saving defaults:', error);
      showHelp('⚠️ Error saving defaults');
    }
  };
  
  // Reset positions to saved defaults
  const handleResetToDefaults = () => {
    if (!currentPlay.defaultPositions || currentPlay.defaultPositions.length === 0) {
      showHelp('⚠️ No defaults saved. Set defaults first!');
      return;
    }
    setCurrentPlay(prev => {
      // Reset player positions
      const newPositions = prev.positions.map(pos => {
        const defaultPos = prev.defaultPositions.find(d => d.id === pos.id || d.positionLabel === pos.positionLabel);
        return defaultPos ? { ...pos, startX: defaultPos.startX, startY: defaultPos.startY, size: defaultPos.size || pos.size } : pos;
      });
      
      // v0.4.5: Reset defender positions too
      let newDefenders = prev.defenders || [];
      if (prev.defaultDefenders && prev.defaultDefenders.length > 0) {
        newDefenders = prev.defaultDefenders.map(dd => {
          const existing = (prev.defenders || []).find(d => d.id === dd.id);
          // v0.5.45: Use guardingId (check both for backwards compatibility)
          return existing ? { ...existing, x: dd.x, y: dd.y, size: dd.size, guardingId: dd.guardingId || dd.guardingPlayerId } : dd;
        });
      }
      
      return {
        ...prev,
        positions: newPositions,
        defenders: newDefenders,
        lastModified: new Date().toISOString()
      };
    });
    showHelp('✅ Positions reset to defaults!');
  };
  
  // Change step number for an action (allows simultaneous actions)
  const handleChangeActionStep = (actionId, actionType, newStep) => {
    if (newStep < 1) newStep = 1;
    setCurrentPlay(prev => {
      const update = { lastModified: new Date().toISOString() };
      if (actionType === 'path') {
        update.paths = (prev.paths || []).map(p => p.id === actionId ? { ...p, stepNumber: newStep } : p);
      } else if (actionType === 'pass') {
        update.passes = (prev.passes || []).map(p => p.id === actionId ? { ...p, stepNumber: newStep } : p);
      } else if (actionType === 'screen') {
        update.screens = (prev.screens || []).map(s => s.id === actionId ? { ...s, stepNumber: newStep } : s);
      } else if (actionType === 'shot') {
        update.shots = (prev.shots || []).map(s => s.id === actionId ? { ...s, stepNumber: newStep } : s);
      } else if (actionType === 'defenderPath') {
        update.defenderPaths = (prev.defenderPaths || []).map(dp => dp.id === actionId ? { ...dp, stepNumber: newStep } : dp);
      }
      return { ...prev, ...update };
    });
  };
  
  // v0.5.35: Select an action from the StepActionsPanel
  const handleActionSelect = (actionId, actionType) => {
    if (selectedActionId === actionId) {
      // Deselect if clicking same action
      setSelectedActionId(null);
      setSelectedActionType(null);
    } else {
      setSelectedActionId(actionId);
      setSelectedActionType(actionType);
      
      // Also highlight the associated player/defender
      if (actionType === 'path' || actionType === 'screen') {
        const action = (currentPlay?.paths || []).find(p => p.id === actionId) ||
                       (currentPlay?.screens || []).find(s => s.id === actionId);
        if (action?.positionId) setSpotlightPlayer(action.positionId);
        if (action?.setterId) setSpotlightPlayer(action.setterId);
      } else if (actionType === 'pass') {
        const pass = (currentPlay?.passes || []).find(p => p.id === actionId);
        if (pass?.fromPositionId) setSpotlightPlayer(pass.fromPositionId);
      } else if (actionType === 'shot') {
        const shot = (currentPlay?.shots || []).find(s => s.id === actionId);
        if (shot?.shooterId) setSpotlightPlayer(shot.shooterId);
      } else if (actionType === 'defenderPath') {
        const defPath = (currentPlay?.defenderPaths || []).find(dp => dp.id === actionId);
        if (defPath?.defenderId) setSelectedDefender(defPath.defenderId);
      }
    }
  };
  
  // v0.5.35: Update action properties (speed, etc.)
  const handleActionUpdate = (actionId, actionType, updates) => {
    setCurrentPlay(prev => {
      const update = { lastModified: new Date().toISOString() };
      if (actionType === 'path') {
        update.paths = (prev.paths || []).map(p => p.id === actionId ? { ...p, ...updates } : p);
      } else if (actionType === 'pass') {
        update.passes = (prev.passes || []).map(p => p.id === actionId ? { ...p, ...updates } : p);
      } else if (actionType === 'screen') {
        update.screens = (prev.screens || []).map(s => s.id === actionId ? { ...s, ...updates } : s);
      } else if (actionType === 'shot') {
        update.shots = (prev.shots || []).map(s => s.id === actionId ? { ...s, ...updates } : s);
      } else if (actionType === 'defenderPath') {
        update.defenderPaths = (prev.defenderPaths || []).map(dp => dp.id === actionId ? { ...dp, ...updates } : dp);
      }
      return { ...prev, ...update };
    });
  };
  
  // v0.5.35: Jump to a specific step
  const handleJumpToStep = (step) => {
    setCurrentStep(step);
    setAnimationProgress(0);
    if (isAnimating) setIsAnimating(false);
  };
  
  // v0.5.36: Delete an action
  const handleActionDelete = (actionId, actionType) => {
    setCurrentPlay(prev => {
      const update = { lastModified: new Date().toISOString() };
      if (actionType === 'path') {
        update.paths = (prev.paths || []).filter(p => p.id !== actionId);
      } else if (actionType === 'pass') {
        update.passes = (prev.passes || []).filter(p => p.id !== actionId);
      } else if (actionType === 'screen') {
        update.screens = (prev.screens || []).filter(s => s.id !== actionId);
      } else if (actionType === 'shot') {
        update.shots = (prev.shots || []).filter(s => s.id !== actionId);
      } else if (actionType === 'defenderPath') {
        update.defenderPaths = (prev.defenderPaths || []).filter(dp => dp.id !== actionId);
      }
      return { ...prev, ...update };
    });
    // Clear selection
    setSelectedActionId(null);
    setSelectedActionType(null);
    showHelp('🗑️ Action deleted');
  };
  
  // v0.5.36: Renumber steps to remove gaps (e.g., 1, 3, 5 → 1, 2, 3)
  const handleRenumberSteps = () => {
    setCurrentPlay(prev => {
      // Collect all used step numbers
      const usedSteps = new Set();
      (prev.paths || []).forEach(p => usedSteps.add(p.stepNumber || 1));
      (prev.passes || []).forEach(p => usedSteps.add(p.stepNumber || 1));
      (prev.screens || []).forEach(s => usedSteps.add(s.stepNumber || 1));
      (prev.shots || []).forEach(s => usedSteps.add(s.stepNumber || 1));
      (prev.defenderPaths || []).forEach(dp => usedSteps.add(dp.stepNumber || 1));
      
      // Create mapping from old step to new step
      const sortedSteps = Array.from(usedSteps).sort((a, b) => a - b);
      const stepMapping = {};
      sortedSteps.forEach((oldStep, index) => {
        stepMapping[oldStep] = index + 1;
      });
      
      // Apply mapping to all actions
      const update = {
        lastModified: new Date().toISOString(),
        paths: (prev.paths || []).map(p => ({ ...p, stepNumber: stepMapping[p.stepNumber || 1] })),
        passes: (prev.passes || []).map(p => ({ ...p, stepNumber: stepMapping[p.stepNumber || 1] })),
        screens: (prev.screens || []).map(s => ({ ...s, stepNumber: stepMapping[s.stepNumber || 1] })),
        shots: (prev.shots || []).map(s => ({ ...s, stepNumber: stepMapping[s.stepNumber || 1] })),
        defenderPaths: (prev.defenderPaths || []).map(dp => ({ ...dp, stepNumber: stepMapping[dp.stepNumber || 1] }))
      };
      
      return { ...prev, ...update };
    });
    
    // Reset to step 1
    setCurrentStep(1);
    setAnimationProgress(0);
    showHelp('🔄 Steps renumbered');
  };
  
  // Path editing handlers
  const handlePathSelect = (pathId) => {
    setSelectedPathId(pathId === selectedPathId ? null : pathId);
  };
  
  // v0.4.9: Enhanced path point drag - when endpoint moves, scale path proportionally
  const handlePathPointDrag = (pathId, pointIndex, x, y) => {
    setCurrentPlay(prev => ({
      ...prev,
      paths: (prev.paths || []).map(p => {
        if (p.id !== pathId) return p;
        const newPoints = [...p.points];
        
        // If dragging the endpoint (last point), scale the entire path proportionally
        if (pointIndex === newPoints.length - 1 && newPoints.length > 2) {
          const start = newPoints[0];
          const oldEnd = newPoints[newPoints.length - 1];
          const newEnd = { x, y };
          
          // Calculate scale factors
          const oldDx = oldEnd.x - start.x;
          const oldDy = oldEnd.y - start.y;
          const newDx = newEnd.x - start.x;
          const newDy = newEnd.y - start.y;
          
          const oldLength = Math.sqrt(oldDx * oldDx + oldDy * oldDy);
          const newLength = Math.sqrt(newDx * newDx + newDy * newDy);
          
          if (oldLength > 0.1) { // Avoid division by near-zero
            const scale = newLength / oldLength;
            
            // Calculate rotation
            const oldAngle = Math.atan2(oldDy, oldDx);
            const newAngle = Math.atan2(newDy, newDx);
            const rotation = newAngle - oldAngle;
            
            // Transform all intermediate points
            for (let i = 1; i < newPoints.length - 1; i++) {
              const pt = newPoints[i];
              // Translate to origin (start point)
              const relX = pt.x - start.x;
              const relY = pt.y - start.y;
              
              // Scale
              const scaledX = relX * scale;
              const scaledY = relY * scale;
              
              // Rotate
              const rotatedX = scaledX * Math.cos(rotation) - scaledY * Math.sin(rotation);
              const rotatedY = scaledX * Math.sin(rotation) + scaledY * Math.cos(rotation);
              
              // Translate back
              newPoints[i] = {
                x: start.x + rotatedX,
                y: start.y + rotatedY
              };
            }
          }
          
          // Set the endpoint
          newPoints[newPoints.length - 1] = newEnd;
        } else {
          // Regular point drag - just move that point
          newPoints[pointIndex] = { x, y };
        }
        
        return { ...p, points: newPoints };
      }),
      lastModified: new Date().toISOString()
    }));
  };
  
  const handlePositionSelect = (position) => {
    setSelectedPosition(position.id);
    if (position.startX === null || position.startY === null) {
      setPlacementMode({ positionId: position.id, color: position.color, label: position.positionLabel });
      showHelp(`📍 Click court to place ${position.positionLabel}`);
    }
  };
  
  const handleAssignPlayer = (position) => { setSelectedPosition(position.id); setShowRosterPicker(true); };
  
  const handlePlayerSelected = (player) => {
    if (!selectedPosition) return;
    const position = currentPlay.positions.find(p => p.id === selectedPosition);
    updatePosition(selectedPosition, {
      playerId: player.id,
      playerName: `${player.firstName} ${player.lastName}`,
      jerseyNumber: player.jerseyNumber,
      avatar: player.avatar
    });
    setPlacementMode({ positionId: selectedPosition, player, color: position.color, label: player.jerseyNumber || position.positionLabel });
    setShowRosterPicker(false);
    showHelp(`📍 Click court to place ${player.firstName}`);
  };
  
  const handleRemovePlayer = (position) => {
    updatePosition(position.id, { playerId: null, playerName: null, jerseyNumber: null, avatar: null });
  };
  
  const handleSwapPlayer = (position) => { setSelectedPosition(position.id); setShowRosterPicker(true); };
  
  const handleMarkShooter = (position) => {
    currentPlay.positions.forEach(pos => {
      if (pos.id !== position.id && pos.isShooter) updatePosition(pos.id, { isShooter: false });
    });
    updatePosition(position.id, { isShooter: !position.isShooter });
  };
  
  const handleCourtClick = (x, y) => {
    // v0.5.72: Defender end position click takes priority
    if (drawingMode?.type === 'defenderEndPosition' && drawingMode.defenderId) {
      handleDefenderEndPositionClick(drawingMode.defenderId, x, y);
      return;
    }
    
    // v0.4.6: Defender path drawing takes priority
    if (defenderDrawingMode && selectedDefender) {
      handleDefenderPathPlace(x, y);
      return;
    }
    
    // Tap-to-path mode takes priority
    if (tapMode) {
      handleTapToPath(x, y);
      return;
    }
    
    if (placementMode) {
      updatePosition(placementMode.positionId, { startX: x, startY: y });
      setPlacementMode(null);
      showHelp('✅ Placed! Select another or draw paths');
    }
  };
  
  const handlePlayerClick = (position) => {
    // If clicking on a player icon from a screen, store the screen location for the next path
    if (position.fromScreen && position.screenLocation) {
      // Store that this player should start their next path from the screen location
      setCurrentPlay(prev => ({
        ...prev,
        positions: prev.positions.map(p => 
          p.id === position.id 
            ? { ...p, nextPathStart: position.screenLocation }
            : p
        )
      }));
      showHelp(`🏃 ${position.positionLabel} selected - draw roll/movement from screen`);
    }
    
    setSelectedPosition(position.id);
    if (drawingMode?.type === 'pass' && passFromPosition) {
      const fromPos = currentPlay.positions.find(p => p.id === passFromPosition);
      if (fromPos && position.id !== passFromPosition) {
        const stepNumber = getNextStepNumber();
        const passType = drawingMode.passType || selectedPassType || 'direct';
        const passLabels = { direct: 'Direct', bounce: 'Bounce', lob: 'Lob' };
        const passLabel = passLabels[passType] || passType;
        const newPass = {
          id: `pass-${Date.now()}`, 
          type: passType,
          from: { x: fromPos.startX, y: fromPos.startY },
          to: { x: position.startX, y: position.startY },
          fromPositionId: passFromPosition, 
          toPositionId: position.id,
          stepNumber
        };
        updatePlay({ passes: [...(currentPlay.passes || []), newPass] });
        setPassFromPosition(null);
        setDrawingMode(null);
        
        // v0.5.66: Auto-update currentStep to show the new pass
        setCurrentStep(stepNumber);
        
        // v0.5.66: Prompt for defender paths if defense is enabled
        const defenders = currentPlay.defenders || [];
        if (defenders.length > 0) {
          showHelp(`✅ Step ${stepNumber}: ${passLabel} Pass ${fromPos.positionLabel}→${position.positionLabel} — Update defender positions if needed`);
        } else {
          showHelp(`✅ Step ${stepNumber}: ${passLabel} Pass from ${fromPos.positionLabel} to ${position.positionLabel}`);
        }
      }
    }
  };
  
  const handlePlayerDrag = (positionId, x, y) => { updatePosition(positionId, { startX: x, startY: y }); };
  
  const handleDefenderDrag = (defenderId, x, y) => {
    setCurrentPlay(prev => ({
      ...prev,
      defenders: (prev.defenders || []).map(d => d.id === defenderId ? { ...d, x, y } : d),
      lastModified: new Date().toISOString()
    }));
  };
  
  // v0.5.72: Handle defender click - select and enter "set end position" mode
  const handleDefenderClick = (defender) => {
    if (selectedDefender === defender.id) {
      // Already selected - deselect and clear mode
      setSelectedDefender(null);
      setDrawingMode(null);
    } else {
      // Select this defender and enter end-position mode
      setSelectedDefender(defender.id);
      setSelectedPosition(null); // Deselect any player
      // v0.5.72: Click-to-set-endpoint mode (not draw mode)
      setDrawingMode({ type: 'defenderEndPosition', defenderId: defender.id });
      showHelp(`📍 ${defender.label || 'Defender'} selected - click court to set end position for Step ${getNextStepNumber()}`);
    }
  };
  
  // v0.5.77: Handle court click for defender end position
  const handleDefenderEndPositionClick = (defenderId, x, y) => {
    const defender = (currentPlay?.defenders || []).find(d => d.id === defenderId);
    if (!defender) {
      setDrawingMode(null);
      setPendingDefender(null);
      return;
    }
    
    // v0.5.81: CRITICAL - Use the stepNumber from drawingMode (set when player moved)
    // This ensures defender is placed on SAME step as the player movement
    const isAfterPlayerMove = drawingMode?.afterPlayerMove;
    const playerStepNumber = drawingMode?.stepNumber;
    
    // If we have the player's stepNumber from drawingMode, use it
    // Otherwise fall back to currentStep (which should be the same after player moved)
    const stepNumber = playerStepNumber || currentStep;
    
    debugLog('DEF_CLICK', 'handleDefenderEndPositionClick', {
      defenderId: defender.label,
      'drawingMode.stepNumber': playerStepNumber,
      'currentStep': currentStep,
      'using stepNumber': stepNumber,
      isAfterPlayerMove
    });
    
    // Get defender's current position (either initial or from previous paths)
    let startX = defender.x;
    let startY = defender.y;
    
    // Check if defender has moved in previous steps
    const existingPaths = (currentPlay?.defenderPaths || [])
      .filter(p => p.defenderId === defenderId && (p.stepNumber || 1) < stepNumber)
      .sort((a, b) => (a.stepNumber || 1) - (b.stepNumber || 1));
    
    if (existingPaths.length > 0) {
      const lastPath = existingPaths[existingPaths.length - 1];
      if (lastPath.points?.length >= 2) {
        const lastPoint = lastPath.points[lastPath.points.length - 1];
        startX = lastPoint.x;
        startY = lastPoint.y;
      }
    }
    
    // Create simple 2-point path (start to end) - invisible but functional
    const newDefPath = {
      id: `defpath-${Date.now()}`,
      defenderId,
      type: 'movement',
      points: [{ x: startX, y: startY }, { x, y }],
      stepNumber
    };
    
    // v0.5.76: Only create path - don't update def.x/def.y so step tracking works correctly
    // The visual position is calculated from paths in BasketballCourt
    setCurrentPlay(prev => ({
      ...prev,
      defenderPaths: [...(prev.defenderPaths || []), newDefPath],
      lastModified: new Date().toISOString()
    }));
    
    const guardingPlayer = currentPlay?.positions?.find(p => p.id === (defender.guardingId || defender.guardingPlayerId));
    
    // v0.5.74: Better feedback message
    if (isAfterPlayerMove && guardingPlayer) {
      showHelp(`✅ Step ${stepNumber}: ${guardingPlayer.positionLabel} moved, ${defender.label || 'defender'} positioned — Ready for next action`);
    } else {
      showHelp(`✅ Step ${stepNumber}: ${defender.label || 'Defender'} moved${guardingPlayer ? ` (guarding ${guardingPlayer.positionLabel})` : ''}`);
    }
    
    // v0.5.74: If not after player move, advance step
    if (!isAfterPlayerMove) {
      setCurrentStep(stepNumber);
    }
    
    // v0.5.76: Clear pending state and drawing mode
    setDrawingMode(null);
    setSelectedDefender(null);
    setPendingDefender(null); // Stop pulsing
  };
  
  // v0.5.71: Handle defender path end - create path from drawn line (kept for backwards compat)
  const handleDefenderPathEnd = (defenderId, points) => {
    if (!defenderId || !points || points.length < 2) {
      setDrawingMode(null);
      return;
    }
    
    const defender = (currentPlay?.defenders || []).find(d => d.id === defenderId);
    if (!defender) {
      setDrawingMode(null);
      return;
    }
    
    const stepNumber = getNextStepNumber();
    
    // Create defender movement path from drawn points
    const newDefPath = {
      id: `defpath-${Date.now()}`,
      defenderId,
      type: 'movement',
      points: points,
      stepNumber
    };
    
    setCurrentPlay(prev => ({
      ...prev,
      defenderPaths: [...(prev.defenderPaths || []), newDefPath],
      lastModified: new Date().toISOString()
    }));
    
    const guardingPlayer = currentPlay?.positions?.find(p => p.id === (defender.guardingId || defender.guardingPlayerId));
    showHelp(`✅ Step ${stepNumber}: ${defender.label || 'Defender'} path drawn${guardingPlayer ? ` (guarding ${guardingPlayer.positionLabel})` : ''}`);
    
    // Clear mode after path is created
    setDrawingMode(null);
    setSelectedDefender(null);
  };
  
  // v0.4.6: Handle screen dragging - updates both screen position AND associated path endpoint
  const handleScreenDrag = (screenId, x, y) => {
    setCurrentPlay(prev => {
      const screen = (prev.screens || []).find(s => s.id === screenId);
      if (!screen) return prev;
      
      const oldX = screen.x;
      const oldY = screen.y;
      
      // Update the screen position
      const newScreens = prev.screens.map(s => 
        s.id === screenId ? { ...s, x, y } : s
      );
      
      // Find and update the associated path (the screen movement path)
      // This is the path where isScreenMove is true and endpoints match the old screen position
      const newPaths = (prev.paths || []).map(p => {
        if (p.isScreenMove && p.positionId === screen.setterId && p.points?.length >= 2) {
          const lastPoint = p.points[p.points.length - 1];
          // Check if this path's endpoint matches the screen's old position
          if (Math.abs(lastPoint.x - oldX) < 1 && Math.abs(lastPoint.y - oldY) < 1) {
            // Update the endpoint to new screen position
            const newPoints = [...p.points];
            newPoints[newPoints.length - 1] = { x, y };
            return { ...p, points: newPoints, screenX: x, screenY: y };
          }
        }
        return p;
      });
      
      return {
        ...prev,
        screens: newScreens,
        paths: newPaths,
        lastModified: new Date().toISOString()
      };
    });
  };
  
  const addDefender = () => {
    const newDefender = {
      id: `defender-${Date.now()}`,
      x: 50, // Center of court
      y: 50,
      label: `D${(currentPlay?.defenders?.length || 0) + 1}`
    };
    setCurrentPlay(prev => ({
      ...prev,
      defenders: [...(prev.defenders || []), newDefender],
      lastModified: new Date().toISOString()
    }));
  };
  
  const removeDefender = (defenderId) => {
    setCurrentPlay(prev => ({
      ...prev,
      defenders: (prev.defenders || []).filter(d => d.id !== defenderId),
      lastModified: new Date().toISOString()
    }));
  };
  
  const handlePathStart = () => {};
  const handlePathPoint = () => {};
  
  // Get next step number
  const getNextStepNumber = () => {
    const maxPathStep = Math.max(0, ...(currentPlay?.paths || []).map(p => p.stepNumber || 0));
    const maxPassStep = Math.max(0, ...(currentPlay?.passes || []).map(p => p.stepNumber || 0));
    const maxScreenStep = Math.max(0, ...(currentPlay?.screens || []).map(s => s.stepNumber || 0));
    const maxShotStep = Math.max(0, ...(currentPlay?.shots || []).map(s => s.stepNumber || 0));
    return Math.max(maxPathStep, maxPassStep, maxScreenStep, maxShotStep) + 1;
  };
  
  // Get who has the ball at current state (after all existing actions)
  const getBallHolderNow = () => {
    if (!currentPlay) return null;
    let ballHolder = currentPlay.positions.find(p => p.hasBall)?.id || currentPlay.positions[0]?.id;
    const events = [];
    
    (currentPlay.paths || []).filter(p => p.type === 'dribble').forEach(p => {
      events.push({ step: p.stepNumber || 1, holder: p.positionId });
    });
    
    (currentPlay.passes || []).forEach(p => {
      events.push({ step: p.stepNumber || 1, holder: p.toPositionId });
    });
    
    events.sort((a, b) => a.step - b.step).forEach(e => { ballHolder = e.holder; });
    return ballHolder;
  };
  
  // Get player's current position after all previous movements
  const getPlayerCurrentPosition = (positionId) => {
    const pos = currentPlay?.positions.find(p => p.id === positionId);
    if (!pos || pos.startX === null) return null;
    
    let currentX = pos.startX;
    let currentY = pos.startY;
    
    const playerPaths = (currentPlay.paths || [])
      .filter(p => p.positionId === positionId && p.points?.length >= 2)
      .sort((a, b) => (a.stepNumber || 1) - (b.stepNumber || 1));
    
    playerPaths.forEach(path => {
      const lastPoint = path.points[path.points.length - 1];
      currentX = lastPoint.x;
      currentY = lastPoint.y;
    });
    
    return { x: currentX, y: currentY };
  };
  
  // Get all other players' current positions (for obstacle detection)
  const getOtherPlayersPositions = (excludePositionId) => {
    return currentPlay?.positions
      .filter(p => p.id !== excludePositionId && p.startX !== null)
      .map(p => getPlayerCurrentPosition(p.id))
      .filter(Boolean) || [];
  };
  
  // Generate intelligent path from start to end, curving around obstacles
  const generateIntelligentPath = (startPos, endPos, pathType, excludePositionId) => {
    const obstacles = getOtherPlayersPositions(excludePositionId);
    const PLAYER_RADIUS = 6; // Clearance around players
    const SCREEN_USE_RADIUS = 18; // How close a screen needs to be to use it
    
    // Get screens that are set by teammates (not by self)
    const getUsableScreens = () => {
      const screens = currentPlay.screens || [];
      return screens.filter(s => s.setterId !== excludePositionId);
    };
    
    // Check if a screen is roughly along the path from start to end
    const findScreensAlongPath = () => {
      const screens = getUsableScreens();
      const usableScreens = [];
      
      for (const screen of screens) {
        // Calculate distance from screen to the line segment
        const dx = endPos.x - startPos.x;
        const dy = endPos.y - startPos.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len === 0) continue;
        
        // Project screen onto the line
        const t = Math.max(0, Math.min(1, ((screen.x - startPos.x) * dx + (screen.y - startPos.y) * dy) / (len * len)));
        const projX = startPos.x + t * dx;
        const projY = startPos.y + t * dy;
        
        // Distance from screen to projected point
        const dist = Math.sqrt((screen.x - projX) ** 2 + (screen.y - projY) ** 2);
        
        // Screen is usable if it's close to the path and between start/end
        if (dist < SCREEN_USE_RADIUS && t > 0.15 && t < 0.85) {
          usableScreens.push({ screen, t, dist, projX, projY });
        }
      }
      
      // Sort by position along path (t value)
      return usableScreens.sort((a, b) => a.t - b.t);
    };
    
    // Check if any player is in the direct path
    const isBlocked = (from, to) => {
      for (const obs of obstacles) {
        const dx = to.x - from.x;
        const dy = to.y - from.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len === 0) continue;
        
        const t = Math.max(0, Math.min(1, ((obs.x - from.x) * dx + (obs.y - from.y) * dy) / (len * len)));
        const projX = from.x + t * dx;
        const projY = from.y + t * dy;
        const dist = Math.sqrt((obs.x - projX) ** 2 + (obs.y - projY) ** 2);
        
        if (dist < PLAYER_RADIUS && t > 0.1 && t < 0.9) {
          return obs;
        }
      }
      return null;
    };
    
    // === X-POINT SYSTEM: Route AROUND screens (use them as picks) ===
    const screensAlongPath = findScreensAlongPath();
    
    if (screensAlongPath.length > 0) {
      // Route AROUND each screen - player uses screen as a pick
      const points = [startPos];
      
      for (const screenData of screensAlongPath) {
        const screen = screenData.screen;
        
        // Calculate perpendicular direction to go AROUND the screen
        // Determine which side of the screen to go (toward the destination)
        const toEnd = { x: endPos.x - screen.x, y: endPos.y - screen.y };
        
        // Get direction from start to screen
        const lastPoint = points[points.length - 1];
        const dx = screen.x - lastPoint.x;
        const dy = screen.y - lastPoint.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        
        if (len > 0) {
          // Perpendicular vector
          const perpX = -dy / len;
          const perpY = dx / len;
          
          // Decide which side based on end position
          const dotProduct = toEnd.x * perpX + toEnd.y * perpY;
          const side = dotProduct > 0 ? 1 : -1;
          
          // Create point that goes AROUND the screen (brushing past it)
          // Offset by ~5 units perpendicular to the path
          const brushPoint = {
            x: screen.x + perpX * 5 * side,
            y: screen.y + perpY * 5 * side
          };
          
          points.push(brushPoint);
        }
      }
      
      points.push(endPos);
      
      // Smooth the path between waypoints
      const smoothedPoints = [];
      for (let i = 0; i < points.length - 1; i++) {
        const from = points[i];
        const to = points[i + 1];
        
        // Add intermediate points for smoother curve
        if (i === 0) smoothedPoints.push(from);
        
        // Check if segment is blocked by players (not screens)
        const blocker = isBlocked(from, to);
        
        if (blocker) {
          // Curve around blocker
          const midX = (from.x + to.x) / 2;
          const midY = (from.y + to.y) / 2;
          const segDx = to.x - from.x;
          const segDy = to.y - from.y;
          const segLen = Math.sqrt(segDx * segDx + segDy * segDy);
          const perpX = -segDy / segLen;
          const perpY = segDx / segLen;
          const obsToMid = { x: blocker.x - midX, y: blocker.y - midY };
          const dotProduct = obsToMid.x * perpX + obsToMid.y * perpY;
          const curveDirection = dotProduct > 0 ? -1 : 1;
          const controlPoint = {
            x: midX + perpX * 8 * curveDirection,
            y: midY + perpY * 8 * curveDirection
          };
          
          // Generate curve
          const steps = 4;
          for (let j = 1; j <= steps; j++) {
            const t = j / steps;
            const t1 = 1 - t;
            const x = t1 * t1 * from.x + 2 * t1 * t * controlPoint.x + t * t * to.x;
            const y = t1 * t1 * from.y + 2 * t1 * t * controlPoint.y + t * t * to.y;
            smoothedPoints.push({ x, y });
          }
        } else {
          smoothedPoints.push(to);
        }
      }
      
      return smoothedPoints;
    }
    
    // === No screens along path - use original obstacle avoidance ===
    const blocker = isBlocked(startPos, endPos);
    
    if (!blocker) {
      return [startPos, endPos];
    }
    
    // Blocked - create a curve around the obstacle
    const midX = (startPos.x + endPos.x) / 2;
    const midY = (startPos.y + endPos.y) / 2;
    const dx = endPos.x - startPos.x;
    const dy = endPos.y - startPos.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    const perpX = -dy / len;
    const perpY = dx / len;
    const obsToMid = { x: blocker.x - midX, y: blocker.y - midY };
    const dotProduct = obsToMid.x * perpX + obsToMid.y * perpY;
    const curveDirection = dotProduct > 0 ? -1 : 1;
    const curveAmount = pathType === 'cut' ? 12 : 10;
    const controlPoint = {
      x: midX + perpX * curveAmount * curveDirection,
      y: midY + perpY * curveAmount * curveDirection
    };
    
    const points = [];
    const steps = 8;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const t1 = 1 - t;
      const x = t1 * t1 * startPos.x + 2 * t1 * t * controlPoint.x + t * t * endPos.x;
      const y = t1 * t1 * startPos.y + 2 * t1 * t * controlPoint.y + t * t * endPos.y;
      points.push({ x, y });
    }
    
    return points;
  };
  
  // Handle tap-to-path: when user taps court after selecting action
  const handleTapToPath = (x, y) => {
    debugLog('TAP_PATH', 'handleTapToPath called', { x, y, tapMode, selectedPosition });
    
    if (!tapMode || !selectedPosition) {
      debugLog('TAP_PATH', 'Early exit - no tapMode or selectedPosition');
      return false;
    }
    
    const position = currentPlay.positions.find(p => p.id === selectedPosition);
    if (!position || position.startX === null) {
      debugLog('TAP_PATH', 'Early exit - position not found or not placed', { positionFound: !!position });
      showHelp('⚠️ Place the player first');
      setTapMode(null);
      return false;
    }
    
    const startPos = getPlayerCurrentPosition(selectedPosition);
    if (!startPos) {
      debugLog('TAP_PATH', 'Early exit - no start position');
      return false;
    }
    
    const endPos = { x, y };
    const pathType = tapMode.type;
    debugLog('TAP_PATH', 'Creating path', { pathType, from: startPos, to: endPos });
    
    // Check ball possession for dribble
    if (pathType === 'dribble') {
      const ballHolder = getBallHolderNow();
      if (ballHolder !== selectedPosition) {
        const ballHolderPlayer = currentPlay.positions.find(p => p.id === ballHolder);
        debugLog('TAP_PATH', 'Dribble rejected - no ball', { ballHolder });
        showHelp(`⚠️ ${position.positionLabel} doesn't have the ball! ${ballHolderPlayer?.positionLabel || 'Someone else'} has it.`);
        setTapMode(null);
        return false;
      }
    }
    
    // Generate intelligent path
    const points = generateIntelligentPath(startPos, endPos, pathType, selectedPosition);
    
    const stepNumber = getNextStepNumber();
    const actionLabels = { movement: 'Move', cut: 'Cut', dribble: 'Dribble' };
    const actionLabel = actionLabels[pathType] || pathType;
    
    const newPath = {
      id: `path-${Date.now()}`,
      positionId: selectedPosition,
      type: pathType,
      points,
      stepNumber,
      hasBall: pathType === 'dribble'
    };
    
    debugLog('TAP_PATH', 'Path created', { pathId: newPath.id, stepNumber, positionId: selectedPosition });
    
    updatePlay({ paths: [...(currentPlay.paths || []), newPath] });
    setTapMode(null);
    setCurrentStep(stepNumber);
    
    // v0.5.79: Check for defender prompt
    const defenders = currentPlay.defenders || [];
    debugLog('DEFENDER', '=== DEFENDER CHECK START ===', {
      positionId: selectedPosition,
      positionLabel: position?.positionLabel,
      defenderCount: defenders.length,
      defenders: defenders.map(d => ({ id: d.id, label: d.label, guardingId: d.guardingId }))
    });
    
    if (defenders.length > 0 && position) {
      const playersOnCourt = (currentPlay.positions || []).filter(p => p.startX !== null && p.startY !== null);
      const playerIndex = playersOnCourt.findIndex(p => p.id === selectedPosition);
      
      debugLog('DEFENDER', 'Index matching', { 
        playerIndex, 
        totalPlayers: playersOnCourt.length,
        totalDefenders: defenders.length,
        playerIds: playersOnCourt.map(p => p.id),
        lookingFor: selectedPosition
      });
      
      let guardingDefender = null;
      
      // Find defender at same index as player
      if (playerIndex >= 0 && playerIndex < defenders.length) {
        guardingDefender = defenders[playerIndex];
        debugLog('DEFENDER', 'Found by index', { defenderLabel: guardingDefender?.label, index: playerIndex });
      }
      
      // Fallback to guardingId match
      if (!guardingDefender) {
        guardingDefender = defenders.find(d => 
          d.guardingId === selectedPosition || d.guardingPlayerId === selectedPosition
        );
        if (guardingDefender) {
          debugLog('DEFENDER', 'Found by guardingId', { defenderLabel: guardingDefender.label });
        }
      }
      
      if (guardingDefender) {
        debugLog('DEFENDER', '✓✓✓ TRIGGERING DEFENDER PROMPT ✓✓✓', { 
          defender: guardingDefender.label,
          forPlayer: position.positionLabel,
          playerStepNumber: stepNumber
        });
        setSelectedDefender(guardingDefender.id);
        setPendingDefender(guardingDefender.id);
        setSelectedPosition(null);
        // v0.5.81: Pass stepNumber explicitly so defender uses SAME step as player
        setDrawingMode({ 
          type: 'defenderEndPosition', 
          defenderId: guardingDefender.id, 
          afterPlayerMove: true, 
          stepNumber 
        });
        showHelp(`⚡ SET DEFENDER POSITION: Click court for ${position.positionLabel}'s defender end location (Step ${stepNumber})`);
        return true;
      } else {
        debugLog('DEFENDER', '✗ No defender found', { playerIndex, defenderCount: defenders.length });
      }
    } else {
      debugLog('DEFENDER', 'Skipping - no defenders or no position', { 
        defenderCount: defenders.length, 
        hasPosition: !!position 
      });
    }
    
    debugLog('DEFENDER', '=== DEFENDER CHECK END (no prompt) ===');
    showHelp(`✅ Step ${stepNumber}: ${actionLabel} by ${position?.positionLabel || 'player'}`);
    return true;
  };
  
  // Start tap-to-path mode for an action
  const handleStartTapPath = (pathType) => {
    if (!selectedPosition) {
      showHelp('⚠️ Select a player first');
      return;
    }
    const position = currentPlay.positions.find(p => p.id === selectedPosition);
    if (!position || position.startX === null) {
      showHelp('⚠️ Place the player first');
      return;
    }
    
    // Check ball possession
    const ballHolder = getBallHolderNow();
    const hasBall = ballHolder === selectedPosition;
    
    // Validate action based on ball possession
    if (pathType === 'dribble' && !hasBall) {
      const ballHolderPlayer = currentPlay.positions.find(p => p.id === ballHolder);
      showHelp(`⚠️ ${position.positionLabel} doesn't have the ball! ${ballHolderPlayer?.positionLabel || 'Someone else'} has it.`);
      return;
    }
    
    if ((pathType === 'movement' || pathType === 'cut') && hasBall) {
      showHelp(`💡 ${position.positionLabel} has the ball - use Dribble, Pass, or Shoot`);
      // Still allow it, just inform them
    }
    
    setTapMode({ type: pathType, positionId: selectedPosition });
    setDrawingMode(null); // Clear old drawing mode
    showHelp(`👆 Tap where ${position.positionLabel} should ${pathType === 'movement' ? 'move' : pathType}`);
  };
  
  // Check if selected player has the ball
  const selectedPlayerHasBall = () => {
    if (!selectedPosition) return false;
    const ballHolder = getBallHolderNow();
    return ballHolder === selectedPosition;
  };
  
  // Handle shoot action
  const handleShoot = () => {
    if (!selectedPosition) { 
      showHelp('⚠️ Select the shooter first'); 
      return; 
    }
    const shooter = currentPlay.positions.find(p => p.id === selectedPosition);
    if (!shooter || shooter.startX === null) { 
      showHelp('⚠️ Place the shooter first'); 
      return; 
    }
    
    // Check if this player has the ball
    const ballHolder = getBallHolderNow();
    if (ballHolder !== selectedPosition) {
      const ballHolderPlayer = currentPlay.positions.find(p => p.id === ballHolder);
      showHelp(`⚠️ ${shooter.positionLabel} doesn't have the ball! ${ballHolderPlayer?.positionLabel || 'Someone else'} has it.`);
      return;
    }
    
    const stepNumber = getNextStepNumber();
    const newShot = {
      id: `shot-${Date.now()}`,
      shooterId: selectedPosition,
      stepNumber
    };
    
    updatePlay({ shots: [...(currentPlay.shots || []), newShot] });
    
    // v0.5.66: Auto-update currentStep to show the shot
    setCurrentStep(stepNumber);
    
    showHelp(`⭐ Step ${stepNumber}: Shot by ${shooter.positionLabel || 'player'}!`);
  };
  
  const handlePathEnd = (positionId, points, pathType) => {
    debugLog('DRAG_PATH', 'handlePathEnd called', { positionId, pathType, pointCount: points?.length });
    
    if (points.length < 2) {
      debugLog('DRAG_PATH', 'Early exit - not enough points');
      return;
    }
    const stepNumber = getNextStepNumber();
    const hasBall = pathType === 'dribble';
    const actionLabels = { movement: 'Move', cut: 'Cut', dribble: 'Dribble' };
    const actionLabel = actionLabels[pathType] || pathType;
    const newPath = { 
      id: `path-${Date.now()}`, 
      positionId, 
      type: pathType, 
      points, 
      stepNumber,
      hasBall
    };
    
    debugLog('DRAG_PATH', 'Path created', { pathId: newPath.id, stepNumber, positionId });
    
    updatePlay({ paths: [...(currentPlay.paths || []), newPath] });
    const position = currentPlay.positions.find(p => p.id === positionId);
    if (position) updatePosition(positionId, { paths: [...(position.paths || []), newPath] });
    setDrawingMode(null);
    setCurrentStep(stepNumber);
    
    // v0.5.79: Check for defender prompt with debug logging
    const defenders = currentPlay.defenders || [];
    
    debugLog('DEFENDER', '=== DEFENDER CHECK START (drag) ===', {
      positionId,
      positionLabel: position?.positionLabel,
      defenderCount: defenders.length,
      defenders: defenders.map(d => ({ id: d.id, label: d.label, guardingId: d.guardingId }))
    });
    
    if (defenders.length > 0 && position) {
      const playersOnCourt = (currentPlay.positions || []).filter(p => p.startX !== null && p.startY !== null);
      const playerIndex = playersOnCourt.findIndex(p => p.id === positionId);
      
      debugLog('DEFENDER', 'Index matching', { 
        playerIndex, 
        totalPlayers: playersOnCourt.length,
        totalDefenders: defenders.length,
        playerIds: playersOnCourt.map(p => p.id),
        lookingFor: positionId
      });
      
      let guardingDefender = null;
      
      // Method 1: Find defender at same index as player
      if (playerIndex >= 0 && playerIndex < defenders.length) {
        guardingDefender = defenders[playerIndex];
        debugLog('DEFENDER', 'Found by index', { defenderLabel: guardingDefender?.label, index: playerIndex });
      }
      
      // Method 2: Fallback to guardingId match
      if (!guardingDefender) {
        guardingDefender = defenders.find(d => 
          d.guardingId === positionId || d.guardingPlayerId === positionId
        );
        if (guardingDefender) {
          debugLog('DEFENDER', 'Found by guardingId', { defenderLabel: guardingDefender.label });
        }
      }
      
      if (guardingDefender) {
        debugLog('DEFENDER', '✓✓✓ TRIGGERING DEFENDER PROMPT ✓✓✓', { 
          defender: guardingDefender.label,
          forPlayer: position.positionLabel,
          playerStepNumber: stepNumber  // This is the step the player moved on
        });
        
        setSelectedDefender(guardingDefender.id);
        setPendingDefender(guardingDefender.id);
        setSelectedPosition(null);
        // v0.5.81: Pass stepNumber explicitly so defender uses SAME step as player
        setDrawingMode({ 
          type: 'defenderEndPosition', 
          defenderId: guardingDefender.id, 
          afterPlayerMove: true, 
          stepNumber  // CRITICAL: This is the player's step number
        });
        showHelp(`⚡ SET DEFENDER POSITION: Click court for ${position.positionLabel}'s defender end location (Step ${stepNumber})`);
        return;
      } else {
        debugLog('DEFENDER', '✗ No defender found', { playerIndex, defenderCount: defenders.length });
      }
    } else {
      debugLog('DEFENDER', 'Skipping - no defenders or no position', { 
        defenderCount: defenders.length, 
        hasPosition: !!position 
      });
    }
    
    debugLog('DEFENDER', '=== DEFENDER CHECK END (no prompt) ===');
    showHelp(`✅ Step ${stepNumber}: ${actionLabel} by ${position?.positionLabel || 'player'}`);
  };
  
  // v0.5.74: Auto-move linked defender to follow player
  const autoMoveLinkedDefender = (defenderId, endX, endY, stepNumber) => {
    const defender = (currentPlay?.defenders || []).find(d => d.id === defenderId);
    if (!defender) return;
    
    // Get defender's current position
    let startX = defender.x;
    let startY = defender.y;
    
    const existingPaths = (currentPlay?.defenderPaths || [])
      .filter(p => p.defenderId === defenderId && (p.stepNumber || 1) < stepNumber)
      .sort((a, b) => (a.stepNumber || 1) - (b.stepNumber || 1));
    
    if (existingPaths.length > 0) {
      const lastPath = existingPaths[existingPaths.length - 1];
      if (lastPath.points?.length >= 2) {
        const lastPoint = lastPath.points[lastPath.points.length - 1];
        startX = lastPoint.x;
        startY = lastPoint.y;
      }
    }
    
    // Create path for defender
    const newDefPath = {
      id: `defpath-${Date.now()}`,
      defenderId,
      type: 'movement',
      points: [{ x: startX, y: startY }, { x: endX, y: endY }],
      stepNumber
    };
    
    // v0.5.76: Only create path - don't update def.x/def.y so step tracking works
    setCurrentPlay(prev => ({
      ...prev,
      defenderPaths: [...(prev.defenderPaths || []), newDefPath],
      lastModified: new Date().toISOString()
    }));
  };
  
  const handleStartDrawPath = (pathType = null) => {
    const usePathType = pathType || selectedPathType;
    if (!selectedPosition) { showHelp('⚠️ Select a player first'); return; }
    const position = currentPlay.positions.find(p => p.id === selectedPosition);
    if (!position || position.startX === null) { showHelp('⚠️ Place the player first'); return; }
    if (pathType) setSelectedPathType(pathType);
    setDrawingMode({ type: 'path', positionId: selectedPosition, pathType: usePathType });
    showHelp(`✏️ Drawing ${usePathType} - drag on court`);
  };
  
  const handleStartDrawPass = (passType = null) => {
    const usePassType = passType || selectedPassType;
    if (!selectedPosition) { showHelp('⚠️ Select the passer first'); return; }
    const position = currentPlay.positions.find(p => p.id === selectedPosition);
    if (!position || position.startX === null) { showHelp('⚠️ Place the passer first'); return; }
    
    // Check if this player has the ball
    const ballHolder = getBallHolderNow();
    if (ballHolder !== selectedPosition) {
      const ballHolderPlayer = currentPlay.positions.find(p => p.id === ballHolder);
      showHelp(`⚠️ ${position.positionLabel} doesn't have the ball! ${ballHolderPlayer?.positionLabel || 'Someone else'} has it.`);
      return;
    }
    
    if (passType) setSelectedPassType(passType);
    setPassFromPosition(selectedPosition);
    setDrawingMode({ type: 'pass', passType: usePassType });
    showHelp('👆 Click the receiver');
  };
  
  // v0.5.1: Screen path end handler - creates screen at end of drawn path
  const handleScreenPathEnd = (positionId, points) => {
    if (points.length < 2) return;
    
    const setter = currentPlay.positions.find(p => p.id === positionId);
    if (!setter) return;
    
    const stepNumber = getNextStepNumber();
    const endPoint = points[points.length - 1];
    const startPoint = points[0];
    
    debugLog('SCREEN', 'handleScreenPathEnd called', { positionId, setterLabel: setter.positionLabel, stepNumber });
    
    // Calculate screen rotation based on path direction
    const dx = endPoint.x - startPoint.x;
    const dy = endPoint.y - startPoint.y;
    
    // v0.5.75: Default to 90 degrees (horizontal bar) - most common orientation
    // Only use 0 degrees if movement is strongly horizontal
    let screenRotation = 90;
    if (Math.abs(dx) > Math.abs(dy) * 2) {
      screenRotation = 0; // Strongly horizontal movement = vertical screen bar
    }
    
    // Create movement path to screen location
    const movePath = {
      id: `path-${Date.now()}`,
      positionId: positionId,
      type: 'movement',
      points: points,
      stepNumber,
      isScreenMove: true,
      screenX: endPoint.x,
      screenY: endPoint.y
    };
    
    // Create screen at path endpoint
    const screenId = `screen-${Date.now() + 1}`;
    const newScreen = { 
      id: screenId, 
      type: 'ball', 
      x: endPoint.x, 
      y: endPoint.y, 
      rotation: screenRotation,
      setterId: positionId,
      stepNumber
    };
    
    updatePlay({ 
      paths: [...(currentPlay.paths || []), movePath],
      screens: [...(currentPlay.screens || []), newScreen] 
    });
    setDrawingMode(null);
    setCurrentStep(stepNumber);
    
    // v0.5.82: Check for defender prompt (same as handlePathEnd)
    const defenders = currentPlay.defenders || [];
    debugLog('DEFENDER', '=== DEFENDER CHECK START (screen) ===', {
      positionId,
      positionLabel: setter?.positionLabel,
      defenderCount: defenders.length
    });
    
    if (defenders.length > 0 && setter) {
      const playersOnCourt = (currentPlay.positions || []).filter(p => p.startX !== null && p.startY !== null);
      const playerIndex = playersOnCourt.findIndex(p => p.id === positionId);
      
      debugLog('DEFENDER', 'Index matching (screen)', { playerIndex, totalPlayers: playersOnCourt.length });
      
      let guardingDefender = null;
      
      // Find defender at same index as player
      if (playerIndex >= 0 && playerIndex < defenders.length) {
        guardingDefender = defenders[playerIndex];
        debugLog('DEFENDER', 'Found by index (screen)', { defenderLabel: guardingDefender?.label });
      }
      
      // Fallback to guardingId match
      if (!guardingDefender) {
        guardingDefender = defenders.find(d => 
          d.guardingId === positionId || d.guardingPlayerId === positionId
        );
      }
      
      if (guardingDefender) {
        debugLog('DEFENDER', '✓✓✓ TRIGGERING DEFENDER PROMPT (screen) ✓✓✓', { 
          defender: guardingDefender.label,
          forPlayer: setter.positionLabel,
          playerStepNumber: stepNumber
        });
        setSelectedDefender(guardingDefender.id);
        setPendingDefender(guardingDefender.id);
        setSelectedPosition(null);
        setDrawingMode({ 
          type: 'defenderEndPosition', 
          defenderId: guardingDefender.id, 
          afterPlayerMove: true, 
          stepNumber 
        });
        showHelp(`⚡ SET DEFENDER POSITION: Click court for ${setter.positionLabel}'s defender end location (Step ${stepNumber})`);
        return;  // Don't show regular success message
      }
    }
    
    showHelp(`✅ Step ${stepNumber}: Screen by ${setter.positionLabel || 'Player'} - adjust rotation in panel`);
    
    // v0.5.40: Auto-focus screen in actions panel for rotation adjustment
    setShowActionsPanel(true);
    setSelectedActionId(screenId);
  };
  
  // v0.5.1: Start drawing screen path (replaces click-to-place)
  const handleStartDrawScreen = () => {
    if (!selectedPosition) {
      showHelp('⚠️ Select a player to set the screen');
      return;
    }
    
    const setter = currentPlay.positions.find(p => p.id === selectedPosition);
    if (!setter || setter.startX === null) {
      showHelp('⚠️ Place the setter on court first');
      return;
    }
    
    // Check if this player has the ball - can't screen for yourself
    const ballHolder = getBallHolderNow();
    if (ballHolder === selectedPosition) {
      showHelp(`⚠️ ${setter.positionLabel} has the ball - can't set a screen!`);
      return;
    }
    
    setDrawingMode({ type: 'screen', positionId: selectedPosition });
    showHelp(`✏️ Draw path to screen location - drag on court`);
  };
  
  // Legacy click-based screen place (kept for compatibility, but now unused)
  const handleScreenPlace = (x, y) => {
    // v0.5.1: Redirect to draw mode
    showHelp('⚠️ Use the Screen button to draw a path to screen location');
    return;;
  };
  
  const handleClearPaths = () => {
    if (window.confirm('Clear all paths?')) {
      updatePlay({ paths: [] });
      currentPlay.positions.forEach(pos => updatePosition(pos.id, { paths: [] }));
      showHelp('🗑️ Paths cleared');
    }
  };
  
  const handleClearPasses = () => {
    if (window.confirm('Clear all passes?')) { updatePlay({ passes: [] }); showHelp('🗑️ Passes cleared'); }
  };
  
  const handleClearShots = () => {
    if (window.confirm('Clear all shots?')) { updatePlay({ shots: [] }); showHelp('🗑️ Shots cleared'); }
  };
  
  // Undo last action (removes most recent path, pass, screen, or shot)
  const handleUndo = () => {
    if (!currentPlay) return;
    
    // Collect all actions with their timestamps
    const actions = [];
    
    (currentPlay.paths || []).forEach(p => {
      const timestamp = parseInt(p.id.split('-')[1]) || 0;
      actions.push({ type: 'path', id: p.id, timestamp });
    });
    
    (currentPlay.passes || []).forEach(p => {
      const timestamp = parseInt(p.id.split('-')[1]) || 0;
      actions.push({ type: 'pass', id: p.id, timestamp });
    });
    
    (currentPlay.screens || []).forEach(s => {
      const timestamp = parseInt(s.id.split('-')[1]) || 0;
      actions.push({ type: 'screen', id: s.id, timestamp });
    });
    
    (currentPlay.shots || []).forEach(s => {
      const timestamp = parseInt(s.id.split('-')[1]) || 0;
      actions.push({ type: 'shot', id: s.id, timestamp });
    });
    
    if (actions.length === 0) {
      showHelp('⚠️ Nothing to undo');
      return;
    }
    
    // Find most recent action
    actions.sort((a, b) => b.timestamp - a.timestamp);
    const lastAction = actions[0];
    
    // Remove it
    switch (lastAction.type) {
      case 'path':
        const pathToRemove = currentPlay.paths.find(p => p.id === lastAction.id);
        updatePlay({ paths: currentPlay.paths.filter(p => p.id !== lastAction.id) });
        // Also remove from position's paths array
        if (pathToRemove) {
          const pos = currentPlay.positions.find(p => p.id === pathToRemove.positionId);
          if (pos) updatePosition(pos.id, { paths: (pos.paths || []).filter(p => p.id !== lastAction.id) });
        }
        showHelp('↩️ Path removed');
        break;
      case 'pass':
        updatePlay({ passes: currentPlay.passes.filter(p => p.id !== lastAction.id) });
        showHelp('↩️ Pass removed');
        break;
      case 'screen':
        // Also remove the associated screen movement path
        const screen = currentPlay.screens.find(s => s.id === lastAction.id);
        let pathsToKeep = currentPlay.paths || [];
        if (screen) {
          // Find and remove any screen move path created at nearly the same time
          pathsToKeep = pathsToKeep.filter(p => {
            if (!p.isScreenMove) return true;
            const pathTime = parseInt(p.id.split('-')[1]) || 0;
            // If within 100ms of screen, it's the associated path
            return Math.abs(pathTime - lastAction.timestamp) > 100;
          });
        }
        updatePlay({ 
          screens: currentPlay.screens.filter(s => s.id !== lastAction.id),
          paths: pathsToKeep
        });
        showHelp('↩️ Screen removed');
        break;
      case 'shot':
        updatePlay({ shots: currentPlay.shots.filter(s => s.id !== lastAction.id) });
        showHelp('↩️ Shot removed');
        break;
    }
  };
  
  const handleCancelDrawing = () => { setDrawingMode(null); setPassFromPosition(null); };
  
  // v0.4.5: Show help with auto-detected type based on emoji
  const showHelp = (message) => {
    if (showHelpBubbles) { 
      // Auto-detect type from message prefix
      let type = 'info';
      if (message.startsWith('✅') || message.startsWith('✓')) type = 'success';
      else if (message.startsWith('⚠️') || message.startsWith('❌')) type = 'warning';
      else if (message.startsWith('💡') || message.startsWith('📍')) type = 'tip';
      
      setHelpMessage({ text: message, type }); 
      setTimeout(() => setHelpMessage(null), 4000); 
    }
  };
  
  // v0.5.94: Export play as animated GIF - with re-entry guard
  const handleExportGif = async () => {
    // Prevent multiple simultaneous exports
    if (exportInProgressRef.current) {
      console.log('[GIF] Export already in progress, ignoring');
      return;
    }
    exportInProgressRef.current = true;
    
    if (!currentPlay || !courtContainerRef.current) {
      showHelp('❌ No play to export');
      exportInProgressRef.current = false;
      return;
    }
    
    const totalSteps = getTotalSteps();
    if (totalSteps === 0) {
      showHelp('❌ Add some actions first');
      exportInProgressRef.current = false;
      return;
    }
    
    // Save current state to restore after export
    const savedStep = currentStep;
    const savedAnimating = isAnimating;
    const savedProgress = animationProgress;
    
    setIsExporting(true);
    setExportProgress(0);
    showHelp('🎬 Recording animation...');
    
    try {
      const imgElement = courtContainerRef.current.querySelector('img');
      
      if (!imgElement) {
        throw new Error('Court image not found');
      }
      
      // High-res settings
      const scale = 2;
      const width = imgElement.clientWidth * scale;
      const height = imgElement.clientHeight * scale;
      
      // Animation settings
      const framesPerStep = 15; // More frames for smoother motion
      const frameDelay = 70; // ms between animation frames
      const holdDelay = 400; // ms to hold at end of each step
      const holdFirstFrame = 1200; // Hold step 0 longer
      const holdLastFrame = 1800; // Hold final frame longer
      
      // Create GIF encoder
      const gif = new GIF({
        workers: 2,
        quality: 8,
        width,
        height,
        workerScript: '/gif.worker.js'
      });
      
      // Create offscreen canvas
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      
      // Load court background image
      const courtImg = new Image();
      courtImg.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        courtImg.onload = resolve;
        courtImg.onerror = reject;
        courtImg.src = imgElement.src;
      });
      
      // Calculate total frames: step 0 + animation frames per step
      const totalFrames = 1 + (totalSteps * framesPerStep);
      let frameCount = 0;
      
      console.log(`[GIF] Starting export: ${totalSteps} steps, ${totalFrames} total frames`);
      
      // Capture step 0 (starting positions - single frame, held longer)
      flushSync(() => {
        setCurrentStep(0);
        setIsAnimating(false);
        setAnimationProgress(0);
      });
      await new Promise(r => setTimeout(r, 50));
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
      
      await captureFrame(ctx, courtImg, width, height, scale, 0, totalSteps, currentPlay.name);
      gif.addFrame(ctx, { copy: true, delay: holdFirstFrame });
      frameCount++;
      setExportProgress(Math.round((frameCount / totalFrames) * 90));
      console.log(`[GIF] Frame ${frameCount}/${totalFrames}: Step 0 (hold ${holdFirstFrame}ms)`);
      
      // Capture each step with animation frames
      for (let step = 1; step <= totalSteps; step++) {
        // Animate through the step
        for (let frame = 0; frame < framesPerStep; frame++) {
          const progress = frame / (framesPerStep - 1); // 0 to 1
          
          // Update state
          flushSync(() => {
            setCurrentStep(step);
            setIsAnimating(true);
            setAnimationProgress(progress);
          });
          
          // Longer wait to ensure React fully commits + browser paints
          await new Promise(r => setTimeout(r, 100));
          await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
          await new Promise(r => setTimeout(r, 50)); // Extra settle time
          
          await captureFrame(ctx, courtImg, width, height, scale, step, totalSteps, currentPlay.name);
          
          // Determine frame delay
          let thisDelay = frameDelay;
          const isLastFrameOfStep = (frame === framesPerStep - 1);
          const isLastStep = (step === totalSteps);
          
          if (isLastFrameOfStep && isLastStep) {
            thisDelay = holdLastFrame;
          } else if (isLastFrameOfStep) {
            thisDelay = holdDelay;
          }
          
          gif.addFrame(ctx, { copy: true, delay: thisDelay });
          
          frameCount++;
          setExportProgress(Math.round((frameCount / totalFrames) * 90));
          console.log(`[GIF] Frame ${frameCount}/${totalFrames}: Step ${step}, progress ${progress.toFixed(3)}${isLastFrameOfStep ? ` (HOLD ${thisDelay}ms)` : ''}`);
        }
      }
      
      console.log(`[GIF] All ${frameCount} frames captured, encoding...`);
      
      setExportProgress(92);
      showHelp('🔄 Encoding GIF...');
      
      // Render GIF
      gif.on('finished', (blob) => {
        console.log('[GIF] Encoding complete, downloading...');
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(currentPlay.name || 'play').replace(/\s+/g, '-')}.gif`;
        a.click();
        URL.revokeObjectURL(url);
        
        // Restore original state
        flushSync(() => {
          setCurrentStep(savedStep);
          setIsAnimating(savedAnimating);
          setAnimationProgress(savedProgress);
        });
        
        setIsExporting(false);
        setExportProgress(0);
        exportInProgressRef.current = false; // Reset guard
        const sizeMB = (blob.size / 1024 / 1024).toFixed(1);
        showHelp(`✅ Exported! (${sizeMB}MB, ${frameCount} frames)`);
      });
      
      gif.on('progress', (p) => {
        setExportProgress(92 + Math.round(p * 8));
      });
      
      gif.render();
      
    } catch (error) {
      console.error('GIF export error:', error);
      setIsExporting(false);
      setExportProgress(0);
      exportInProgressRef.current = false; // Reset guard
      
      flushSync(() => {
        setCurrentStep(savedStep);
        setIsAnimating(savedAnimating);
        setAnimationProgress(savedProgress);
      });
      
      showHelp(`❌ Export failed: ${error.message}`);
    }
  };
  
  // Helper function to capture a single frame
  const captureFrame = async (ctx, courtImg, width, height, scale, step, totalSteps, playName) => {
    // Clear and draw court background
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(courtImg, 0, 0, width, height);
    
    // Get current SVG and render it
    const svg = courtContainerRef.current.querySelector('svg');
    if (svg) {
      // Force layout reflow to ensure DOM is updated
      void svg.getBoundingClientRect();
      
      const svgClone = svg.cloneNode(true);
      svgClone.setAttribute('width', width);
      svgClone.setAttribute('height', height);
      
      const svgData = new XMLSerializer().serializeToString(svgClone);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);
      
      const svgImg = new Image();
      await new Promise((resolve, reject) => {
        svgImg.onload = resolve;
        svgImg.onerror = reject;
        svgImg.src = svgUrl;
      });
      
      ctx.drawImage(svgImg, 0, 0, width, height);
      URL.revokeObjectURL(svgUrl);
    }
    
    // Step indicator (top-left)
    const stepText = `Step ${step} of ${totalSteps}`;
    ctx.font = `bold ${22 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
    const stepMetrics = ctx.measureText(stepText);
    const stepBoxWidth = stepMetrics.width + 24 * scale;
    const stepBoxHeight = 36 * scale;
    
    ctx.fillStyle = 'rgba(15, 23, 42, 0.85)';
    ctx.beginPath();
    ctx.roundRect(12 * scale, 12 * scale, stepBoxWidth, stepBoxHeight, 8 * scale);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.fillText(stepText, 24 * scale, 38 * scale);
    
    // Play name (bottom-left)
    const name = playName || 'Untitled Play';
    ctx.font = `600 ${18 * scale}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
    const nameMetrics = ctx.measureText(name);
    const nameBoxWidth = Math.min(nameMetrics.width + 24 * scale, width - 24 * scale);
    const nameBoxHeight = 32 * scale;
    
    ctx.fillStyle = 'rgba(15, 23, 42, 0.85)';
    ctx.beginPath();
    ctx.roundRect(12 * scale, height - 12 * scale - nameBoxHeight, nameBoxWidth, nameBoxHeight, 8 * scale);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.fillText(name.substring(0, 35), 24 * scale, height - 24 * scale);
  };
  
  const toggleCourtView = () => { updatePlay({ courtView: currentPlay.courtView === 'half' ? 'full' : 'half' }); };
  
  // Handle color settings change
  const handleColorsChange = (newColors) => {
    setColorSettings(newColors);
    
    // Update player position colors in current play
    if (currentPlay && newColors.positions) {
      const positionMap = { 1: 'PG', 2: 'SG', 3: 'SF', 4: 'PF', 5: 'C' };
      const updatedPositions = currentPlay.positions.map(pos => {
        const posLabel = positionMap[pos.positionNumber] || pos.positionLabel;
        const newColor = newColors.positions[posLabel];
        return newColor ? { ...pos, color: newColor } : pos;
      });
      updatePlay({ positions: updatedPositions });
    }
    
    showHelp('🎨 Colors updated!');
  };
  
  const getAssignedPlayerIds = () => currentPlay?.positions.filter(p => p.playerId).map(p => p.playerId) || [];
  const getAllPaths = () => currentPlay?.paths || [];
  const placedCount = currentPlay?.positions?.filter(p => p.startX !== null).length || 0;
  
  // Step management functions
  const handleAddStep = () => {
    if (!newStepText.trim()) return;
    const steps = currentPlay.steps || [];
    updatePlay({ 
      steps: [...steps, { 
        id: `step-${Date.now()}`, 
        number: steps.length + 1, 
        text: newStepText.trim() 
      }] 
    });
    setNewStepText('');
  };
  
  const handleUpdateStep = (index, text) => {
    const steps = [...(currentPlay.steps || [])];
    steps[index] = { ...steps[index], text };
    updatePlay({ steps });
    setEditingStepIndex(null);
  };
  
  const handleDeleteStep = (index) => {
    const steps = [...(currentPlay.steps || [])];
    steps.splice(index, 1);
    // Renumber remaining steps
    steps.forEach((step, i) => { step.number = i + 1; });
    updatePlay({ steps });
  };
  
  const handleMoveStep = (index, direction) => {
    const steps = [...(currentPlay.steps || [])];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= steps.length) return;
    [steps[index], steps[newIndex]] = [steps[newIndex], steps[index]];
    // Renumber
    steps.forEach((step, i) => { step.number = i + 1; });
    updatePlay({ steps });
  };
  
  return (
    <div style={styles.container} data-element="play-designer-root">
      {/* v0.5.84: DEFENDER POSITION PROMPT - TOP BANNER (doesn't block court) */}
      {pendingDefender && drawingMode?.type === 'defenderEndPosition' && (
        <div data-element="play-designer-defender-prompt" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
          padding: '15px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '15px',
          zIndex: 9999,
          boxShadow: '0 4px 20px rgba(220, 38, 38, 0.5)',
          animation: 'pulse 1s infinite'
        }}>
          <div style={{ fontSize: '32px' }}>🔴</div>
          <div>
            <div style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>
              SET DEFENDER POSITION
            </div>
            <div style={{ color: '#fca5a5', fontSize: '14px' }}>
              Click on the court where the defender should end up
            </div>
          </div>
          <button 
            onClick={() => { setDrawingMode(null); setPendingDefender(null); setSelectedDefender(null); }}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: '#fff',
              padding: '8px 16px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Skip
          </button>
        </div>
      )}
      
      {/* v0.5.40: Redesigned Professional Header */}
      {currentPlay && view === 'designer' && (
        <div data-element="play-designer-header" style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          padding: '0',
          borderBottom: '1px solid #334155'
        }}>
          {/* Top Row - Play Info */}
          <div data-element="play-designer-header-top" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 20px',
            gap: '16px'
          }}>
            {/* Left: Back + Play Type */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }} data-element="play-designer-header-left">
              <button 
                onClick={() => setView('library')}
                data-element="play-designer-back-btn"
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  padding: '8px 14px',
                  color: '#fff',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s ease'
                }}
              >
                ← Back
              </button>
              
              <div data-element="play-designer-type-badge" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                background: currentPlay.type === 'offense' ? 'rgba(59, 130, 246, 0.2)' : 
                           currentPlay.type === 'defense' ? 'rgba(220, 38, 38, 0.2)' : 
                           currentPlay.type === 'outofbounds' ? 'rgba(139, 92, 246, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                borderRadius: '20px',
                border: `1px solid ${currentPlay.type === 'offense' ? '#3b82f6' : 
                        currentPlay.type === 'defense' ? '#dc2626' : 
                        currentPlay.type === 'outofbounds' ? '#8b5cf6' : '#f59e0b'}`
              }}>
                <span style={{ fontSize: '14px' }}>
                  {currentPlay.type === 'offense' ? '⚔️' : currentPlay.type === 'defense' ? '🛡️' : currentPlay.type === 'outofbounds' ? '📍' : '⚡'}
                </span>
                <select 
                  value={currentPlay.type} 
                  onChange={e => updatePlay({ type: e.target.value, category: '' })}
                  data-element="play-designer-type-select"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#fff',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    outline: 'none',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  <option value="offense" style={{ background: '#1e293b' }}>Offense</option>
                  <option value="defense" style={{ background: '#1e293b' }}>Defense</option>
                  <option value="transition" style={{ background: '#1e293b' }}>Transition</option>
                  <option value="outofbounds" style={{ background: '#1e293b' }}>Out of Bounds</option>
                </select>
              </div>
            </div>
            
            {/* Center: Play Name */}
            <div style={{ flex: 1, maxWidth: '400px', textAlign: 'center' }} data-element="play-designer-name-container">
              <input 
                type="text" 
                value={currentPlay.name} 
                onChange={e => updatePlay({ name: e.target.value })}
                data-element="play-designer-name-input"
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '18px',
                  fontWeight: '700',
                  textAlign: 'center',
                  outline: 'none',
                  letterSpacing: '0.5px'
                }}
                placeholder="Play name..."
              />
            </div>
            
            {/* Right: Save + Options */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }} data-element="play-designer-header-right">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button 
                  data-element="play-designer-debug-btn"
                  style={{
                    ...styles.modeButton, 
                    backgroundColor: assignmentMode === 'template' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(255,255,255,0.1)',
                    color: assignmentMode === 'template' ? '#93c5fd' : '#94a3b8',
                    border: assignmentMode === 'template' ? '1px solid #3b82f6' : '1px solid rgba(255,255,255,0.1)',
                    padding: '6px 10px',
                    fontSize: '11px'
                  }} 
                  onClick={() => setAssignmentMode('template')}
                >
                  📋 Template
                </button>
                <button 
                  style={{
                    ...styles.modeButton, 
                    backgroundColor: assignmentMode === 'players' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(255,255,255,0.1)',
                    color: assignmentMode === 'players' ? '#86efac' : '#94a3b8',
                    border: assignmentMode === 'players' ? '1px solid #22c55e' : '1px solid rgba(255,255,255,0.1)',
                    padding: '6px 10px',
                    fontSize: '11px'
                  }} 
                  onClick={() => setAssignmentMode('players')}
                >
                  👥 Players
                </button>
              </div>
              
              <div style={{ width: '1px', height: '24px', background: 'rgba(255,255,255,0.2)' }} />
              
              <button 
                onClick={handleSave} 
                disabled={isSaving}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                  opacity: isSaving ? 0.7 : 1
                }}
              >
                {isSaving ? '⏳' : '💾'} Save
              </button>
              
              {/* v0.5.86: GIF Export / Share button */}
              <button 
                onClick={handleExportGif} 
                disabled={isExporting || getTotalSteps() === 0}
                style={{
                  background: isExporting 
                    ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' 
                    : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: (isExporting || getTotalSteps() === 0) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)',
                  opacity: (isExporting || getTotalSteps() === 0) ? 0.7 : 1,
                  position: 'relative',
                  overflow: 'hidden'
                }}
                title={getTotalSteps() === 0 ? 'Add actions to export' : 'Export as animated GIF'}
              >
                {isExporting ? (
                  <>
                    <span style={{ 
                      position: 'absolute', 
                      left: 0, 
                      top: 0, 
                      bottom: 0, 
                      background: 'rgba(255,255,255,0.3)', 
                      width: `${exportProgress}%`,
                      transition: 'width 0.2s'
                    }} />
                    <span style={{ position: 'relative' }}>🎬 {exportProgress}%</span>
                  </>
                ) : (
                  <>📤 Share</>
                )}
              </button>
            </div>
          </div>
          
          {/* Bottom Row - Tools */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '8px 20px',
            background: 'rgba(0,0,0,0.2)',
            borderTop: '1px solid rgba(255,255,255,0.05)',
            overflowX: 'auto'
          }}>
            {/* Court View */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>COURT</span>
              <button 
                onClick={toggleCourtView}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '6px',
                  padding: '5px 10px',
                  color: '#fff',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                {currentPlay.courtView === 'half' ? '◻️ Half' : '📐 Full'}
              </button>
            </div>
            
            <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)' }} />
            
            {/* Positions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>POSITIONS</span>
              <button 
                onClick={handleSetDefaultPositions}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '6px',
                  padding: '5px 10px',
                  color: '#94a3b8',
                  fontSize: '11px',
                  cursor: 'pointer'
                }}
                title="Save current positions as defaults"
              >
                📌 Save
              </button>
              <button 
                onClick={handleResetToDefaults}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '6px',
                  padding: '5px 10px',
                  color: '#94a3b8',
                  fontSize: '11px',
                  cursor: 'pointer'
                }}
                title="Reset to saved default positions"
              >
                ↩️ Reset
              </button>
            </div>
            
            <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)' }} />
            
            {/* Category */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>CATEGORY</span>
              <select 
                value={currentPlay.category} 
                onChange={e => updatePlay({ category: e.target.value })}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '6px',
                  padding: '5px 10px',
                  color: '#fff',
                  fontSize: '12px',
                  cursor: 'pointer',
                  outline: 'none'
                }}
              >
                <option value="" style={{ background: '#1e293b' }}>Select...</option>
                {PLAY_CATEGORIES[currentPlay.type]?.map(cat => (
                  <option key={cat.id} value={cat.id} style={{ background: '#1e293b' }}>{cat.label}</option>
                ))}
              </select>
            </div>
            
            <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)' }} />
            
            {/* Shot Clock */}
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px', 
              cursor: 'pointer',
              fontSize: '11px',
              color: currentPlay.shotClock?.enabled ? '#f59e0b' : '#64748b'
            }}>
              <input 
                type="checkbox" 
                checked={currentPlay.shotClock?.enabled || false} 
                onChange={e => updatePlay({ shotClock: { ...currentPlay.shotClock, enabled: e.target.checked } })}
                style={{ accentColor: '#f59e0b' }}
              />
              ⏱️ Shot Clock
            </label>
            
            <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)' }} />
            
            {/* Colors */}
            <button 
              onClick={() => setShowColorSettings(true)}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '6px',
                padding: '5px 10px',
                color: '#94a3b8',
                fontSize: '11px',
                cursor: 'pointer'
              }}
            >
              🎨 Colors
            </button>
            
            {/* Help Toggle */}
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px', 
              cursor: 'pointer',
              fontSize: '11px',
              color: showHelpBubbles ? '#3b82f6' : '#64748b',
              marginLeft: 'auto'
            }}>
              <input 
                type="checkbox" 
                checked={showHelpBubbles} 
                onChange={e => setShowHelpBubbles(e.target.checked)}
                style={{ accentColor: '#3b82f6' }}
              />
              💡 Help
            </label>
          </div>
        </div>
      )}
      
      <div style={styles.content} data-element="play-designer-content">
        {view === 'library' ? (
          <PlayLibrary plays={plays} selectedPlay={currentPlay} onSelectPlay={handleSelectPlay} onCreateNew={handleCreateNew} onImportPlay={handleImportPlay} onDuplicatePlay={handleDuplicatePlay} onDeletePlay={handleDeletePlay} onExportPlay={handleExportPlay} isLoading={isLoading} />
        ) : currentPlay ? (
          <div style={styles.designerLayout} data-element="play-designer-layout">
            
            {/* ACTION TOOLBAR - Desktop: horizontal bar, Tablet: hidden (use floating overlay) */}
            {!isTablet && (
            <div style={styles.drawingToolbar} data-element="play-designer-toolbar">
              {!selectedPosition ? (
                <span style={{ color: '#6b7280', fontSize: '14px', padding: '8px' }} data-element="play-designer-toolbar-hint">👆 Select a player to see actions</span>
              ) : (() => {
                const hasBall = selectedPlayerHasBall();
                const selectedPlayer = currentPlay.positions.find(p => p.id === selectedPosition);
                const playerLabel = selectedPlayer?.positionLabel || 'Player';
                
                return hasBall ? (
                  // PLAYER HAS BALL: Dribble, Pass, Shoot
                  <>
                    <span style={styles.toolbarLabel}>🏀 {playerLabel} has ball:</span>
                    
                    <button 
                      style={{ ...styles.actionButton, ...(drawingMode?.pathType === 'dribble' ? { background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: '#fff' } : {}) }} 
                      onClick={() => handleStartDrawPath('dribble')}
                    >
                      🏀 Dribble
                    </button>
                    
                    <div style={styles.toolbarDivider} />
                    
                    <span style={styles.toolbarLabel}>Pass:</span>
                    <button 
                      style={{ ...styles.actionButton, ...(drawingMode?.passType === 'direct' ? { background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', color: '#fff' } : {}) }} 
                      onClick={() => handleStartDrawPass('direct')}
                    >
                      ➡️ Direct
                    </button>
                    <button 
                      style={{ ...styles.actionButton, ...(drawingMode?.passType === 'bounce' ? { background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', color: '#fff' } : {}) }} 
                      onClick={() => handleStartDrawPass('bounce')}
                    >
                      ⤵️ Bounce
                    </button>
                    <button 
                      style={{ ...styles.actionButton, ...(drawingMode?.passType === 'lob' ? { background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', color: '#fff' } : {}) }} 
                      onClick={() => handleStartDrawPass('lob')}
                    >
                      🌈 Lob
                    </button>
                    
                    <div style={styles.toolbarDivider} />
                    
                    <button 
                      style={{ ...styles.actionButton, background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', color: '#fff' }} 
                      onClick={handleShoot}
                    >
                      ⭐ Shoot
                    </button>
                  </>
                ) : (
                  // PLAYER DOESN'T HAVE BALL: Move, Cut, Screen
                  <>
                    <span style={styles.toolbarLabel}>{playerLabel}:</span>
                    
                    <button 
                      style={{ ...styles.actionButton, ...(drawingMode?.pathType === 'movement' ? { background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: '#fff' } : {}) }} 
                      onClick={() => handleStartDrawPath('movement')}
                    >
                      👟 Move
                    </button>
                    <button 
                      style={{ ...styles.actionButton, ...(drawingMode?.pathType === 'cut' ? { background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', color: '#fff' } : {}) }} 
                      onClick={() => handleStartDrawPath('cut')}
                    >
                      ⏩ Cut
                    </button>
                    
                    <div style={styles.toolbarDivider} />
                    
                    <button 
                      style={{ ...styles.actionButton, ...(drawingMode?.type === 'screen' ? { background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', color: '#fff' } : {}) }} 
                      onClick={handleStartDrawScreen}
                    >
                      🛡️ Screen
                    </button>
                  </>
                );
              })()}
              
              <div style={styles.toolbarDivider} data-element="play-toolbar-divider-main" />
              
              {/* Undo button - always visible */}
              <button 
                style={{ ...styles.actionButton }} 
                onClick={handleUndo}
                title="Undo last action"
                data-element="play-toolbar-undo-btn"
              >
                ↩️ Undo
              </button>
              
              {/* Clear/Cancel */}
              <button style={styles.clearButtonDark} onClick={handleClearPaths} data-element="play-toolbar-clear-paths-btn">🗑️ ({currentPlay.paths?.length || 0})</button>
              <button style={styles.clearButtonDark} onClick={handleClearPasses} data-element="play-toolbar-clear-passes-btn">🗑️ ({currentPlay.passes?.length || 0})</button>
              <button style={styles.clearButtonDark} onClick={handleClearShots} data-element="play-toolbar-clear-shots-btn">⭐ ({currentPlay.shots?.length || 0})</button>
              {drawingMode && <button style={styles.cancelButtonDark} onClick={handleCancelDrawing} data-element="play-toolbar-cancel-btn">✕</button>}
            </div>
            )}
            
            <div style={styles.designerArea} data-element="play-designer-area">
              {/* v0.5.51: Step Actions Panel (left side when visible) */}
              {totalSteps > 0 && (
                <StepActionsPanel
                  currentPlay={currentPlay}
                  currentStep={currentStep}
                  totalSteps={totalSteps}
                  animationProgress={animationProgress}
                  isExpanded={showActionsPanel}
                  onExpandToggle={() => setShowActionsPanel(!showActionsPanel)}
                  selectedActionId={selectedActionId}
                  onActionSelect={handleActionSelect}
                  onActionUpdate={handleActionUpdate}
                  onActionDelete={handleActionDelete}
                  onStepChange={handleChangeActionStep}
                  onJumpToStep={handleJumpToStep}
                  onRenumberSteps={handleRenumberSteps}
                  positions={currentPlay.positions}
                  defenders={currentPlay.defenders || []}
                  onPlayerUpdate={handlePlayerUpdate}
                  onDefenderUpdate={handleDefenderUpdate}
                  onSpotlightChange={setSpotlightPlayer}
                />
              )}
              
              <div style={styles.courtSection} data-element="play-designer-court-section">
                {/* v0.4.0: Grid and Snap Toggle Controls (hidden on tablet) */}
                {!isTablet && (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '6px', justifyContent: 'center' }} data-element="play-designer-court-controls">
                  <button 
                    onClick={() => setShowGrid(!showGrid)}
                    data-element="play-designer-grid-toggle"
                    style={{
                      padding: '4px 8px',
                      fontSize: '11px',
                      backgroundColor: showGrid ? '#3b82f6' : '#e5e7eb',
                      color: showGrid ? '#fff' : '#374151',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                    title="Toggle debug grid"
                  >
                    📐 Grid {showGrid ? 'ON' : 'OFF'}
                  </button>
                  <button 
                    onClick={() => setSnapEnabled(!snapEnabled)}
                    data-element="play-designer-snap-toggle"
                    style={{
                      padding: '4px 8px',
                      fontSize: '11px',
                      backgroundColor: snapEnabled ? '#10b981' : '#e5e7eb',
                      color: snapEnabled ? '#fff' : '#374151',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                    title="Toggle snap to grid"
                  >
                    🧲 Snap {snapEnabled ? 'ON' : 'OFF'}
                  </button>
                  {/* Toggle Actions Panel Button */}
                  {totalSteps > 0 && !showActionsPanel && (
                    <button 
                      onClick={() => setShowActionsPanel(true)}
                      data-element="play-designer-actions-panel-toggle"
                      style={{
                        padding: '4px 8px',
                        fontSize: '11px',
                        backgroundColor: '#8b5cf6',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      title="Show actions panel"
                    >
                      📋 Actions
                    </button>
                  )}
                </div>
                )}
                
                <div style={styles.courtWrapper} ref={courtContainerRef} data-element="play-designer-court-wrapper">
                  <BasketballCourt 
                    courtView={currentPlay.courtView} 
                    positions={currentPlay.positions} 
                    paths={getAllPaths()} 
                    screens={currentPlay.screens || []} 
                    passes={currentPlay.passes || []} 
                    shots={currentPlay.shots || []} 
                    defenders={showDefenders ? (currentPlay.defenders || []) : []} 
                    defenderPaths={currentPlay.defenderPaths || []}
                    selectedPosition={selectedPosition} 
                    selectedDefender={selectedDefender}
                    selectedPath={selectedPathId} 
                    placementMode={placementMode} 
                    drawingMode={drawingMode} 
                    tapMode={tapMode} 
                    currentStep={currentStep} 
                    animationProgress={animationProgress} 
                    isAnimating={isAnimating} 
                    lineColors={colorSettings.lines}
                    showGrid={showGrid}
                    snapEnabled={snapEnabled}
                    spotlightPlayer={spotlightPlayer}
                    showJerseys={showJerseys}
                    jerseyTemplate={jerseyTemplate}
                    defenderJerseyColor={defenderJerseyColor}
                    onCourtClick={handleCourtClick} 
                    onPlayerClick={handlePlayerClick} 
                    onPlayerDrag={handlePlayerDrag} 
                    onDefenderClick={handleDefenderClick}
                    onDefenderDrag={handleDefenderDrag} 
                    onScreenDrag={handleScreenDrag}
                    onPathStart={handlePathStart} 
                    onPathPoint={handlePathPoint} 
                    onPathEnd={handlePathEnd} 
                    onDefenderPathEnd={handleDefenderPathEnd}
                    onScreenPathEnd={handleScreenPathEnd}
                    onScreenPlace={handleScreenPlace} 
                    onPathSelect={handlePathSelect} 
                    onPathPointDrag={handlePathPointDrag}
                    onSpotlightChange={setSpotlightPlayer}
                  />
                  {currentPlay.shotClock?.enabled && <ShotClock enabled={true} mode="duration" currentTime={(currentStep + animationProgress) * 1000} countdownFrom={24000} position="top-right" size="medium" />}
                  {tapMode && <div style={styles.drawingIndicator} data-element="play-drawing-indicator-tap">👆 Tap destination for {tapMode.type === 'movement' ? 'Move' : tapMode.type === 'cut' ? 'Cut' : 'Dribble'}</div>}
                  {defenderDrawingMode && <div style={styles.drawingIndicator} data-element="play-drawing-indicator-defender">📍 Click destination for defender move</div>}
                  {!tapMode && !defenderDrawingMode && drawingMode && <div style={styles.drawingIndicator} data-element="play-drawing-indicator-mode">{drawingMode.type === 'path' ? `✏️ Drawing ${drawingMode.pathType}` : drawingMode.type === 'pass' ? `➡️ ${drawingMode.passType || 'Direct'} pass - click receiver` : drawingMode.type === 'defenderPath' ? '🛡️ Drag to draw defender path' : drawingMode.type === 'defenderEndPosition' ? `🔴 Click court for defender position` : drawingMode.type === 'screen' ? '🛡️ Draw path to screen location' : '🛡️ Click to place screen'}</div>}
                  
                  {/* v0.5.138: Tablet Floating Action Overlay */}
                  {isTablet && selectedPosition && (() => {
                    const hasBall = selectedPlayerHasBall();
                    const selectedPlayer = currentPlay.positions.find(p => p.id === selectedPosition);
                    const playerLabel = selectedPlayer?.positionLabel || 'Player';
                    
                    return (
                      <div style={styles.floatingActionOverlay} data-element="tablet-action-overlay">
                        <div style={styles.floatingActionHeader}>
                          {hasBall ? `🏀 ${playerLabel}` : playerLabel}
                        </div>
                        {hasBall ? (
                          // PLAYER HAS BALL: Dribble, Pass, Shoot
                          <>
                            <button 
                              style={{...styles.floatingActionBtn, ...(drawingMode?.pathType === 'dribble' ? styles.floatingActionBtnActive : {})}}
                              onClick={() => handleStartDrawPath('dribble')}
                            >
                              🏀 Dribble
                            </button>
                            <div style={styles.floatingActionDivider} />
                            <button 
                              style={{...styles.floatingActionBtn, ...(drawingMode?.passType === 'direct' ? styles.floatingActionBtnActiveGreen : {})}}
                              onClick={() => handleStartDrawPass('direct')}
                            >
                              ➡️ Pass
                            </button>
                            <button 
                              style={{...styles.floatingActionBtn, ...(drawingMode?.passType === 'bounce' ? styles.floatingActionBtnActiveGreen : {})}}
                              onClick={() => handleStartDrawPass('bounce')}
                            >
                              ⤵️ Bounce
                            </button>
                            <button 
                              style={{...styles.floatingActionBtn, ...(drawingMode?.passType === 'lob' ? styles.floatingActionBtnActiveGreen : {})}}
                              onClick={() => handleStartDrawPass('lob')}
                            >
                              🌈 Lob
                            </button>
                            <div style={styles.floatingActionDivider} />
                            <button 
                              style={{...styles.floatingActionBtn, background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'}}
                              onClick={handleShoot}
                            >
                              ⭐ Shoot
                            </button>
                          </>
                        ) : (
                          // NO BALL: Move, Cut, Screen
                          <>
                            <button 
                              style={{...styles.floatingActionBtn, ...(drawingMode?.pathType === 'movement' ? styles.floatingActionBtnActiveBlue : {})}}
                              onClick={() => handleStartDrawPath('movement')}
                            >
                              👟 Move
                            </button>
                            <button 
                              style={{...styles.floatingActionBtn, ...(drawingMode?.pathType === 'cut' ? styles.floatingActionBtnActiveRed : {})}}
                              onClick={() => handleStartDrawPath('cut')}
                            >
                              ⏩ Cut
                            </button>
                            <div style={styles.floatingActionDivider} />
                            <button 
                              style={{...styles.floatingActionBtn, ...(drawingMode?.type === 'screen' ? styles.floatingActionBtnActivePurple : {})}}
                              onClick={handleStartDrawScreen}
                            >
                              🛡️ Screen
                            </button>
                          </>
                        )}
                        {drawingMode && (
                          <>
                            <div style={styles.floatingActionDivider} />
                            <button 
                              style={{...styles.floatingActionBtn, background: 'rgba(239, 68, 68, 0.3)', color: '#fca5a5'}}
                              onClick={handleCancelDrawing}
                            >
                              ✕ Cancel
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })()}
                  
                  {/* Tablet: Hint when no player selected */}
                  {isTablet && !selectedPosition && (
                    <div style={styles.floatingHint} data-element="tablet-select-hint">
                      👆 Tap a player to see actions
                    </div>
                  )}
                </div>
                
                {/* v0.4.4: Player Focus Row - unified selection + spotlight */}
                {/* v0.5.139: On tablet, positioned as floating overlay */}
                {isTablet ? (
                  <div style={{
                    position: 'fixed',
                    bottom: '120px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 50
                  }}>
                    <PlayerFocusRow
                      positions={currentPlay.positions}
                      spotlightPlayer={spotlightPlayer}
                      selectedPosition={selectedPosition}
                      currentBallHolder={getBallHolderAtStep(currentStep)}
                      onSpotlightChange={setSpotlightPlayer}
                      onPositionSelect={(posId) => setSelectedPosition(posId)}
                      isAnimating={isAnimating}
                      isTablet={true}
                    />
                  </div>
                ) : (
                  <PlayerFocusRow
                    positions={currentPlay.positions}
                    spotlightPlayer={spotlightPlayer}
                    selectedPosition={selectedPosition}
                    currentBallHolder={getBallHolderAtStep(currentStep)}
                    onSpotlightChange={setSpotlightPlayer}
                    onPositionSelect={(posId) => setSelectedPosition(posId)}
                    isAnimating={isAnimating}
                    isTablet={false}
                  />
                )}
                
                {/* v0.5.18: Modern Playback Controls */}
                <div className="playback-controls" data-element="play-designer-playback-controls" style={{
                  background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                  borderRadius: '16px',
                  padding: '16px 20px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)',
                  marginTop: '8px',
                  flexShrink: 0
                }}>
                  {/* Main Transport Controls */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: totalSteps > 0 ? '16px' : '0' }} data-element="play-designer-transport-controls">
                    {/* Reset Button */}
                    <button 
                      onClick={handleResetAnimation}
                      title="Reset to start"
                      data-element="play-designer-reset-btn"
                      style={{
                        width: '40px', height: '40px',
                        borderRadius: '10px',
                        border: 'none',
                        background: 'linear-gradient(145deg, #334155, #1e293b)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)',
                        cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round">
                        <polygon points="19 20 9 12 19 4 19 20" fill="#94a3b8"/>
                        <line x1="5" y1="19" x2="5" y2="5"/>
                      </svg>
                    </button>
                    
                    {/* Step Back */}
                    <button 
                      onClick={handleStepBack}
                      disabled={currentStep === 0 && animationProgress === 0}
                      title="Previous step"
                      data-element="play-designer-step-back-btn"
                      style={{
                        width: '40px', height: '40px',
                        borderRadius: '10px',
                        border: 'none',
                        background: 'linear-gradient(145deg, #334155, #1e293b)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)',
                        cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
                        opacity: currentStep === 0 && animationProgress === 0 ? 0.5 : 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={e => { if(currentStep > 0) e.currentTarget.style.transform = 'scale(1.05)'; }}
                      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round">
                        <polygon points="11 19 2 12 11 5 11 19" fill="#94a3b8"/>
                        <polygon points="22 19 13 12 22 5 22 19" fill="#94a3b8"/>
                      </svg>
                    </button>
                    
                    {/* Play/Pause - Hero Button */}
                    <button 
                      onClick={handlePlayAnimation}
                      title={isAnimating ? 'Pause' : 'Play'}
                      data-element="play-designer-play-pause-btn"
                      data-playing={isAnimating}
                      style={{
                        width: '56px', height: '56px',
                        borderRadius: '50%',
                        border: 'none',
                        background: isAnimating 
                          ? 'linear-gradient(145deg, #f59e0b, #d97706)' 
                          : 'linear-gradient(145deg, #22c55e, #16a34a)',
                        boxShadow: isAnimating
                          ? '0 4px 20px rgba(245, 158, 11, 0.4), inset 0 2px 0 rgba(255,255,255,0.2)'
                          : '0 4px 20px rgba(34, 197, 94, 0.4), inset 0 2px 0 rgba(255,255,255,0.2)',
                        cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
                      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      {isAnimating ? (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff">
                          <rect x="6" y="4" width="4" height="16" rx="1"/>
                          <rect x="14" y="4" width="4" height="16" rx="1"/>
                        </svg>
                      ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff">
                          <polygon points="8 5 20 12 8 19 8 5"/>
                        </svg>
                      )}
                    </button>
                    
                    {/* Step Forward */}
                    <button 
                      onClick={handleStepForward}
                      disabled={currentStep >= totalSteps}
                      title="Next step"
                      data-element="play-designer-step-forward-btn"
                      style={{
                        width: '40px', height: '40px',
                        borderRadius: '10px',
                        border: 'none',
                        background: 'linear-gradient(145deg, #334155, #1e293b)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)',
                        cursor: currentStep >= totalSteps ? 'not-allowed' : 'pointer',
                        opacity: currentStep >= totalSteps ? 0.5 : 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={e => { if(currentStep < totalSteps) e.currentTarget.style.transform = 'scale(1.05)'; }}
                      onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round">
                        <polygon points="13 19 22 12 13 5 13 19" fill="#94a3b8"/>
                        <polygon points="2 19 11 12 2 5 2 19" fill="#94a3b8"/>
                      </svg>
                    </button>
                    
                    {/* Step Counter Display */}
                    <div data-element="play-designer-step-counter" style={{
                      background: 'rgba(30, 41, 59, 0.8)',
                      borderRadius: '12px',
                      padding: '8px 16px',
                      display: 'flex', alignItems: 'center', gap: '8px',
                      border: '1px solid rgba(148, 163, 184, 0.2)'
                    }}>
                      <span style={{ color: '#64748b', fontSize: '12px', fontWeight: '500' }}>STEP</span>
                      <span data-element="play-designer-current-step" style={{ 
                        color: '#3b82f6', 
                        fontSize: '24px', 
                        fontWeight: '700',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        minWidth: '24px',
                        textAlign: 'center'
                      }}>{currentStep}</span>
                      <span style={{ color: '#475569', fontSize: '16px' }}>/</span>
                      <span data-element="play-designer-total-steps" style={{ color: '#94a3b8', fontSize: '18px', fontWeight: '600' }}>{totalSteps}</span>
                    </div>
                    
                    {/* Speed Control */}
                    <div data-element="play-designer-speed-control" style={{
                      marginLeft: 'auto',
                      background: 'rgba(30, 41, 59, 0.8)',
                      borderRadius: '12px',
                      padding: '8px 12px',
                      display: 'flex', alignItems: 'center', gap: '8px',
                      border: '1px solid rgba(148, 163, 184, 0.2)'
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12 6 12 12 16 14"/>
                      </svg>
                      <button 
                        onClick={() => setAnimationSpeed(Math.max(1, animationSpeed - 1))}
                        disabled={animationSpeed <= 1}
                        style={{
                          width: '28px', height: '28px',
                          borderRadius: '6px',
                          border: 'none',
                          background: animationSpeed <= 1 ? '#1e293b' : 'linear-gradient(145deg, #475569, #334155)',
                          color: animationSpeed <= 1 ? '#475569' : '#e2e8f0',
                          fontSize: '18px',
                          fontWeight: '600',
                          cursor: animationSpeed <= 1 ? 'not-allowed' : 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}
                      >−</button>
                      <div style={{
                        minWidth: '32px',
                        textAlign: 'center',
                        fontSize: '16px',
                        fontWeight: '700',
                        color: animationSpeed <= 3 ? '#ef4444' : animationSpeed <= 6 ? '#f59e0b' : '#22c55e'
                      }}>
                        {animationSpeed}x
                      </div>
                      <button 
                        onClick={() => setAnimationSpeed(Math.min(10, animationSpeed + 1))}
                        disabled={animationSpeed >= 10}
                        style={{
                          width: '28px', height: '28px',
                          borderRadius: '6px',
                          border: 'none',
                          background: animationSpeed >= 10 ? '#1e293b' : 'linear-gradient(145deg, #475569, #334155)',
                          color: animationSpeed >= 10 ? '#475569' : '#e2e8f0',
                          fontSize: '18px',
                          fontWeight: '600',
                          cursor: animationSpeed >= 10 ? 'not-allowed' : 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}
                      >+</button>
                    </div>
                    
                    {/* Version Badge */}
                    <div style={{ color: '#475569', fontSize: '10px', fontWeight: '500' }}>v0.5.98</div>
                  </div>
                  
                  {/* Quick Step Buttons - Shows what happens at each step (hidden on tablet) */}
                  {totalSteps > 0 && !isTablet && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '4px', 
                        marginBottom: '8px',
                        color: '#64748b',
                        fontSize: '11px',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                        </svg>
                        Quick Steps
                      </div>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {Array.from({ length: totalSteps }, (_, i) => i + 1).map(step => {
                          const isCurrentStep = currentStep === step;
                          const isPastStep = currentStep > step;
                          
                          // Find what happens at this step
                          const stepPaths = currentPlay.paths?.filter(p => p.stepNumber === step) || [];
                          const stepPasses = currentPlay.passes?.filter(p => p.stepNumber === step) || [];
                          const stepScreens = currentPlay.screens?.filter(s => s.stepNumber === step) || [];
                          const stepDefPaths = currentPlay.defenderPaths?.filter(p => p.stepNumber === step) || [];
                          
                          const offenseActions = stepPaths.length + stepPasses.length + stepScreens.length;
                          const defenseActions = stepDefPaths.length;
                          
                          // Get player labels for this step
                          const playerLabels = [
                            ...stepPaths.map(p => currentPlay.positions?.find(pos => pos.id === p.positionId)?.positionLabel).filter(Boolean),
                            ...stepPasses.map(p => currentPlay.positions?.find(pos => pos.id === p.fromPositionId)?.positionLabel).filter(Boolean)
                          ];
                          
                          return (
                            <button
                              key={step}
                              onClick={() => {
                                setCurrentStep(step);
                                setAnimationProgress(0);
                                if (isAnimating) setIsAnimating(false);
                              }}
                              title={`Step ${step}: ${offenseActions} offense, ${defenseActions} defense actions`}
                              style={{
                                minWidth: '64px',
                                padding: '8px 12px',
                                borderRadius: '10px',
                                border: isCurrentStep ? '2px solid #3b82f6' : '1px solid rgba(148, 163, 184, 0.2)',
                                background: isCurrentStep 
                                  ? 'linear-gradient(145deg, #1e40af, #1d4ed8)' 
                                  : isPastStep 
                                    ? 'rgba(34, 197, 94, 0.15)' 
                                    : 'rgba(30, 41, 59, 0.6)',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '2px'
                              }}
                              onMouseEnter={e => {
                                if (!isCurrentStep) {
                                  e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)';
                                  e.currentTarget.style.borderColor = '#3b82f6';
                                }
                              }}
                              onMouseLeave={e => {
                                if (!isCurrentStep) {
                                  e.currentTarget.style.background = isPastStep ? 'rgba(34, 197, 94, 0.15)' : 'rgba(30, 41, 59, 0.6)';
                                  e.currentTarget.style.borderColor = 'rgba(148, 163, 184, 0.2)';
                                }
                              }}
                            >
                              <span style={{ 
                                fontSize: '14px', 
                                fontWeight: '700', 
                                color: isCurrentStep ? '#fff' : isPastStep ? '#22c55e' : '#94a3b8' 
                              }}>
                                {step}
                              </span>
                              <div style={{ display: 'flex', gap: '4px', fontSize: '10px' }}>
                                {offenseActions > 0 && (
                                  <span style={{ 
                                    background: '#3b82f6', 
                                    color: '#fff', 
                                    padding: '1px 4px', 
                                    borderRadius: '4px',
                                    fontWeight: '600'
                                  }}>
                                    {offenseActions}⚪
                                  </span>
                                )}
                                {defenseActions > 0 && (
                                  <span style={{ 
                                    background: '#dc2626', 
                                    color: '#fff', 
                                    padding: '1px 4px', 
                                    borderRadius: '4px',
                                    fontWeight: '600'
                                  }}>
                                    {defenseActions}🔴
                                  </span>
                                )}
                              </div>
                              {playerLabels.length > 0 && (
                                <span style={{ 
                                  fontSize: '9px', 
                                  color: isCurrentStep ? '#93c5fd' : '#64748b',
                                  maxWidth: '60px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {playerLabels.slice(0, 2).join(', ')}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* v0.5.1: Dual Timeline Scrubber with 0.25 increments */}
                {/* v0.5.113: Hidden - set false to true to re-enable */}
                {false && totalSteps > 0 && (
                  <div style={styles.timelineContainer} data-element="play-timeline-container">
                    {/* Timeline labels */}
                    <div style={{ display: 'flex', marginBottom: '4px', fontSize: '10px', fontWeight: '600' }} data-element="play-timeline-labels">
                      <span style={{ color: '#3b82f6', flex: 1 }} data-element="play-timeline-offense-label">⚪ OFFENSE</span>
                      <span style={{ color: '#dc2626', flex: 1, textAlign: 'right' }} data-element="play-timeline-defense-label">🔴 DEFENSE</span>
                    </div>
                    
                    {/* Offense Track (Blue) */}
                    <div style={{ marginBottom: '8px' }} data-element="play-timeline-offense-track-wrapper">
                      <div 
                        style={{ ...styles.timelineTrack, backgroundColor: '#1e3a5f' }}
                        data-element="play-timeline-offense-track"
                        onClick={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const clickX = e.clientX - rect.left;
                          const percent = clickX / rect.width;
                          // Snap to 0.25 increments
                          const rawValue = percent * totalSteps;
                          const snappedValue = Math.round(rawValue * 4) / 4;
                          const targetStep = Math.floor(snappedValue);
                          const stepProgress = snappedValue - targetStep;
                          setCurrentStep(Math.max(0, Math.min(totalSteps, targetStep)));
                          setAnimationProgress(stepProgress);
                          if (isAnimating) setIsAnimating(false);
                        }}
                        onMouseDown={(e) => {
                          const track = e.currentTarget;
                          const handleDrag = (moveEvent) => {
                            const rect = track.getBoundingClientRect();
                            const dragX = moveEvent.clientX - rect.left;
                            const percent = Math.max(0, Math.min(1, dragX / rect.width));
                            // Continuous scrubbing with 0.25 snap on release
                            const rawValue = percent * totalSteps;
                            const targetStep = Math.floor(rawValue);
                            const stepProgress = rawValue - targetStep;
                            setCurrentStep(Math.max(0, Math.min(totalSteps, targetStep)));
                            setAnimationProgress(stepProgress);
                            if (isAnimating) setIsAnimating(false);
                          };
                          const handleUp = () => {
                            // Snap to 0.25 on release
                            const current = currentStep + animationProgress;
                            const snapped = Math.round(current * 4) / 4;
                            setCurrentStep(Math.floor(snapped));
                            setAnimationProgress(snapped - Math.floor(snapped));
                            document.removeEventListener('mousemove', handleDrag);
                            document.removeEventListener('mouseup', handleUp);
                          };
                          document.addEventListener('mousemove', handleDrag);
                          document.addEventListener('mouseup', handleUp);
                        }}
                      >
                        {/* Progress fill */}
                        <div style={{ ...styles.timelineProgress, backgroundColor: '#3b82f6', width: `${((currentStep + animationProgress) / totalSteps) * 100}%` }} data-element="play-timeline-offense-progress" />
                        
                        {/* 0.25 increment tick marks */}
                        {Array.from({ length: totalSteps * 4 + 1 }, (_, i) => {
                          const value = i / 4;
                          const isWholeStep = i % 4 === 0;
                          return (
                            <div
                              key={i}
                              style={{
                                position: 'absolute',
                                left: `${(value / totalSteps) * 100}%`,
                                top: isWholeStep ? '0' : '25%',
                                height: isWholeStep ? '100%' : '50%',
                                width: isWholeStep ? '2px' : '1px',
                                backgroundColor: value <= (currentStep + animationProgress) ? '#60a5fa' : '#4b5563',
                                transform: 'translateX(-50%)',
                                zIndex: 1
                              }}
                            />
                          );
                        })}
                        
                        {/* Step number labels */}
                        {Array.from({ length: totalSteps + 1 }, (_, i) => (
                          <div
                            key={`label-${i}`}
                            style={{
                              position: 'absolute',
                              left: `${(i / totalSteps) * 100}%`,
                              bottom: '-16px',
                              transform: 'translateX(-50%)',
                              fontSize: '9px',
                              color: '#9ca3af',
                              fontWeight: i === currentStep ? '700' : '400'
                            }}
                          >
                            {i}
                          </div>
                        ))}
                        
                        {/* Playhead */}
                        <div style={{ ...styles.timelinePlayhead, backgroundColor: '#3b82f6', left: `${((currentStep + animationProgress) / totalSteps) * 100}%` }} data-element="play-timeline-offense-playhead" />
                      </div>
                    </div>
                    
                    {/* Defense Track (Red) */}
                    <div style={{ marginTop: '20px' }} data-element="play-timeline-defense-track-wrapper">
                      <div 
                        style={{ ...styles.timelineTrack, backgroundColor: '#4a1515' }}
                        data-element="play-timeline-defense-track"
                        onClick={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          const clickX = e.clientX - rect.left;
                          const percent = clickX / rect.width;
                          const rawValue = percent * totalSteps;
                          const snappedValue = Math.round(rawValue * 4) / 4;
                          const targetStep = Math.floor(snappedValue);
                          const stepProgress = snappedValue - targetStep;
                          setCurrentStep(Math.max(0, Math.min(totalSteps, targetStep)));
                          setAnimationProgress(stepProgress);
                          if (isAnimating) setIsAnimating(false);
                        }}
                        onMouseDown={(e) => {
                          const track = e.currentTarget;
                          const handleDrag = (moveEvent) => {
                            const rect = track.getBoundingClientRect();
                            const dragX = moveEvent.clientX - rect.left;
                            const percent = Math.max(0, Math.min(1, dragX / rect.width));
                            const rawValue = percent * totalSteps;
                            const targetStep = Math.floor(rawValue);
                            const stepProgress = rawValue - targetStep;
                            setCurrentStep(Math.max(0, Math.min(totalSteps, targetStep)));
                            setAnimationProgress(stepProgress);
                            if (isAnimating) setIsAnimating(false);
                          };
                          const handleUp = () => {
                            const current = currentStep + animationProgress;
                            const snapped = Math.round(current * 4) / 4;
                            setCurrentStep(Math.floor(snapped));
                            setAnimationProgress(snapped - Math.floor(snapped));
                            document.removeEventListener('mousemove', handleDrag);
                            document.removeEventListener('mouseup', handleUp);
                          };
                          document.addEventListener('mousemove', handleDrag);
                          document.addEventListener('mouseup', handleUp);
                        }}
                      >
                        {/* Progress fill */}
                        <div style={{ ...styles.timelineProgress, backgroundColor: '#dc2626', width: `${((currentStep + animationProgress) / totalSteps) * 100}%` }} data-element="play-timeline-defense-progress" />
                        
                        {/* 0.25 increment tick marks */}
                        {Array.from({ length: totalSteps * 4 + 1 }, (_, i) => {
                          const value = i / 4;
                          const isWholeStep = i % 4 === 0;
                          return (
                            <div
                              key={i}
                              style={{
                                position: 'absolute',
                                left: `${(value / totalSteps) * 100}%`,
                                top: isWholeStep ? '0' : '25%',
                                height: isWholeStep ? '100%' : '50%',
                                width: isWholeStep ? '2px' : '1px',
                                backgroundColor: value <= (currentStep + animationProgress) ? '#f87171' : '#4b5563',
                                transform: 'translateX(-50%)',
                                zIndex: 1
                              }}
                            />
                          );
                        })}
                        
                        {/* Playhead */}
                        <div style={{ ...styles.timelinePlayhead, backgroundColor: '#dc2626', left: `${((currentStep + animationProgress) / totalSteps) * 100}%` }} data-element="play-timeline-defense-playhead" />
                      </div>
                    </div>
                    
                    {/* Time display */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }} data-element="play-timeline-info">
                      <span style={styles.timelineHint} data-element="play-timeline-time-display">
                        Time: {(currentStep + animationProgress).toFixed(2)} / {totalSteps}
                      </span>
                      <span style={styles.timelineHint} data-element="play-timeline-hint">
                        Drag to scrub (snaps to 0.25) • Pause to adjust players/defenders
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* v0.5.136: Side Panel Toggle for iPad/Tablet */}
              {isTablet && (
                <button
                  style={{
                    ...styles.sidePanelToggle,
                    right: showSidePanel ? '288px' : '8px'
                  }}
                  onClick={() => setShowSidePanel(!showSidePanel)}
                  title={showSidePanel ? 'Hide Panel' : 'Show Panel'}
                >
                  {showSidePanel ? '▶' : '◀ Players'}
                </button>
              )}
              
              <div style={{...styles.sidePanel, ...(!showSidePanel ? styles.sidePanelCollapsed : {})}}>
                {/* v0.5.20: Redesigned SidePanel with per-player speed */}
                <SidePanel
                  positions={currentPlay.positions}
                  screens={currentPlay.screens || []}
                  defenders={currentPlay.defenders || []}
                  defenderPaths={currentPlay.defenderPaths || []}
                  paths={currentPlay.paths || []}
                  passes={currentPlay.passes || []}
                  selectedPosition={selectedPosition}
                  selectedDefender={selectedDefender}
                  pendingDefender={pendingDefender}
                  playerSize={playerSize}
                  defenderSize={defenderSize}
                  showDefenders={showDefenders}
                  showJerseys={showJerseys}
                  rosterPlayers={rosterPlayers}
                  defenderJerseyColor={defenderJerseyColor}
                  opponents={opponents}
                  selectedOpponentId={selectedOpponentId}
                  currentStep={currentStep}
                  onShowDefendersChange={setShowDefenders}
                  onShowJerseysChange={setShowJerseys}
                  onDefenderJerseyColorChange={setDefenderJerseyColor}
                  onSelectedOpponentChange={(opponentId) => {
                    setSelectedOpponentId(opponentId);
                    if (opponentId) {
                      const opponent = opponents.find(o => o.id === opponentId);
                      if (opponent?.fillColor) {
                        setDefenderJerseyColor(opponent.fillColor);
                      }
                    }
                  }}
                  onPositionSelect={handlePositionSelect}
                  onPlayerSizeChange={handlePlayerGroupSizeChange}
                  onDefenderSizeChange={handleDefenderGroupSizeChange}
                  onScreenUpdate={handleScreenUpdate}
                  onPlayerUpdate={handlePlayerUpdate}
                  onDefenderUpdate={handleDefenderUpdate}
                  onDefenderSelect={setSelectedDefender}
                  onDefenderPathStart={handleStartDefenderPath}
                  onAddDefender={handleAddDefenderFromPanel}
                  onRemoveDefender={handleRemoveDefenderFromPanel}
                />
                
                {/* v0.5.19: Simplified status - most controls now in SidePanel */}
                <div style={styles.statusPanel} data-element="play-status-panel">
                  <p style={styles.statusText} data-element="play-status-placed">⚪ {placedCount}/5 Placed</p>
                  <p style={styles.statusText} data-element="play-status-paths">➡️ {currentPlay.paths?.length || 0} Paths</p>
                  <p style={styles.statusText} data-element="play-status-passes">📤 {currentPlay.passes?.length || 0} Passes</p>
                  <p style={styles.statusText} data-element="play-status-screens">🛡️ {currentPlay.screens?.length || 0} Screens</p>
                </div>
                
                {/* v0.5.79: Debug Mode Toggle & Panel */}
                <div style={{ padding: '4px 8px', borderTop: '1px solid #374151' }} data-element="play-debug-toggle-container">
                  <button
                    onClick={() => setDebugMode(!debugMode)}
                    data-element="play-debug-toggle-btn"
                    style={{
                      width: '100%',
                      padding: '4px 8px',
                      fontSize: '10px',
                      background: debugMode ? '#dc2626' : '#374151',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    🔧 Debug: {debugMode ? 'ON' : 'OFF'}
                  </button>
                </div>
                
                {debugMode && (
                  <div style={{ 
                    padding: '8px', 
                    borderTop: '1px solid #374151',
                    background: '#1e1e1e',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }} data-element="play-debug-panel">
                    <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
                      <button
                        onClick={copyDebugLog}
                        data-element="play-debug-copy-btn"
                        style={{
                          flex: 1,
                          padding: '6px',
                          fontSize: '11px',
                          background: '#2563eb',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        📋 Copy Log ({debugLogRef.current.length})
                      </button>
                      <button
                        onClick={clearDebugLog}
                        data-element="play-debug-clear-btn"
                        style={{
                          padding: '6px 10px',
                          fontSize: '11px',
                          background: '#dc2626',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                    <div style={{ 
                      fontFamily: 'monospace', 
                      fontSize: '9px', 
                      color: '#9ca3af',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all'
                    }}>
                      {debugLogRef.current.slice(-20).map((e, i) => (
                        <div key={i} style={{ 
                          marginBottom: '4px', 
                          padding: '2px 4px',
                          background: e.category === 'DEFENDER' ? '#1e3a2f' : '#252525',
                          borderLeft: e.message.includes('✓✓✓') ? '2px solid #22c55e' : 
                                     e.message.includes('✗') ? '2px solid #ef4444' : '2px solid #4b5563'
                        }}>
                          <span style={{ color: '#6b7280' }}>[{e.time}]</span>{' '}
                          <span style={{ color: '#f59e0b' }}>[{e.category}]</span>{' '}
                          <span style={{ color: e.message.includes('✓') ? '#22c55e' : '#e5e5e5' }}>{e.message}</span>
                          {e.data && <pre style={{ color: '#94a3b8', margin: '2px 0 0 0', fontSize: '8px' }}>{e.data}</pre>}
                        </div>
                      ))}
                      {debugLogRef.current.length === 0 && (
                        <div style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
                          No logs yet. Draw a path to see defender check logs.
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* v0.5.49: Version badge */}
                <div style={styles.versionBadge} data-element="play-version-badge">v0.5.98</div>
              </div>
            </div>
            
            {/* v0.5.45: Step-specific notes - only show when on a step (hidden on tablet) */}
            {currentStep > 0 && !isTablet && (
              <div style={styles.stepNotesSection} data-element="play-step-notes-section">
                <div style={styles.stepNotesHeader} data-element="play-step-notes-header">
                  <span style={styles.stepNotesLabel} data-element="play-step-notes-label">📝 Step {currentStep} Notes</span>
                  <button 
                    style={styles.stepNotesClear}
                    data-element="play-step-notes-clear-btn"
                    onClick={() => {
                      const newStepNotes = { ...(currentPlay.stepNotes || {}) };
                      delete newStepNotes[currentStep];
                      updatePlay({ stepNotes: newStepNotes });
                    }}
                    title="Clear note"
                  >✕</button>
                </div>
                <textarea 
                  value={(currentPlay.stepNotes || {})[currentStep] || ''} 
                  onChange={e => {
                    const newStepNotes = { ...(currentPlay.stepNotes || {}), [currentStep]: e.target.value };
                    updatePlay({ stepNotes: newStepNotes });
                  }} 
                  placeholder={`Add notes for step ${currentStep}...`} 
                  style={styles.stepNotesInput}
                  data-element="play-step-notes-input"
                />
              </div>
            )}
            
            {/* Step 0 info */}
            {currentStep === 0 && (
              <div style={styles.stepZeroInfo} data-element="play-step-zero-info">
                <span data-element="play-step-zero-title">📍 Initial Positions (Starting Lineup)</span>
                <span style={{ fontSize: '12px', color: '#6b7280' }} data-element="play-step-zero-hint">Press ▶️ or → to advance to Step 1</span>
              </div>
            )}
            
            {/* Play Steps Panel */}
            {/* v0.5.113: Hidden - set false to true to re-enable */}
            {false && (
            <div style={styles.stepsSection} data-element="play-steps-section">
              <div style={styles.stepsHeader} data-element="play-steps-header">
                <h3 style={styles.stepsTitle} data-element="play-steps-title">📝 Play Steps</h3>
                <span style={styles.stepsCount} data-element="play-steps-count">{(currentPlay.steps || []).length} steps</span>
              </div>
              
              {/* Existing steps list */}
              <div style={styles.stepsList} data-element="play-steps-list">
                {(currentPlay.steps || []).map((step, index) => (
                  <div key={step.id} style={styles.stepItem} data-element={`play-step-${step.id}`}>
                    <span style={styles.stepNumber} data-element={`play-step-${step.id}-number`}>{step.number}</span>
                    {editingStepIndex === index ? (
                      <input
                        type="text"
                        defaultValue={step.text}
                        autoFocus
                        style={styles.stepEditInput}
                        data-element={`play-step-${step.id}-edit-input`}
                        onBlur={(e) => handleUpdateStep(index, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleUpdateStep(index, e.target.value);
                          if (e.key === 'Escape') setEditingStepIndex(null);
                        }}
                      />
                    ) : (
                      <span 
                        style={styles.stepText} 
                        onClick={() => setEditingStepIndex(index)}
                        title="Click to edit"
                        data-element={`play-step-${step.id}-text`}
                      >
                        {step.text}
                      </span>
                    )}
                    <div style={styles.stepActions} data-element={`play-step-${step.id}-actions`}>
                      <button style={styles.stepActionBtn} onClick={() => handleMoveStep(index, -1)} disabled={index === 0} title="Move up" data-element={`play-step-${step.id}-move-up-btn`}>↑</button>
                      <button style={styles.stepActionBtn} onClick={() => handleMoveStep(index, 1)} disabled={index === (currentPlay.steps || []).length - 1} title="Move down" data-element={`play-step-${step.id}-move-down-btn`}>↓</button>
                      <button style={{...styles.stepActionBtn, color: '#dc2626'}} onClick={() => handleDeleteStep(index)} title="Delete" data-element={`play-step-${step.id}-delete-btn`}>×</button>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Add new step */}
              <div style={styles.addStepRow} data-element="play-steps-add-row">
                <span style={styles.stepNumber} data-element="play-steps-add-number">{(currentPlay.steps || []).length + 1}</span>
                <input
                  type="text"
                  value={newStepText}
                  onChange={(e) => setNewStepText(e.target.value)}
                  placeholder="Add step (e.g., 'PG dribbles to wing')"
                  style={styles.addStepInput}
                  data-element="play-steps-add-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddStep(); }}
                />
                <button style={styles.addStepBtn} onClick={handleAddStep} disabled={!newStepText.trim()} data-element="play-steps-add-btn">+ Add</button>
              </div>
            </div>
            )}
          </div>
        ) : (
          <div style={styles.emptyState} data-element="play-empty-state"><span style={styles.emptyIcon} data-element="play-empty-icon">🏀</span><span style={styles.emptyText} data-element="play-empty-text">Select or create a play</span><button style={styles.emptyButton} onClick={handleCreateNew} data-element="play-empty-create-btn">+ Create New Play</button></div>
        )}
      </div>
      
      {helpMessage && <FloatingHelpBubble message={helpMessage.text || helpMessage} isVisible={true} type={helpMessage.type || 'info'} autoHide={true} autoHideDelay={4000} onDismiss={() => setHelpMessage(null)} />}
      <RosterPicker isOpen={showRosterPicker} onClose={() => setShowRosterPicker(false)} onSelectPlayer={handlePlayerSelected} currentPosition={currentPlay?.positions.find(p => p.id === selectedPosition)} excludePlayerIds={getAssignedPlayerIds()} />
      <ColorSettings 
        isOpen={showColorSettings} 
        onClose={() => setShowColorSettings(false)} 
        onColorsChange={handleColorsChange} 
      />
    </div>
  );
};

const styles = {
  container: { display: 'flex', flexDirection: 'column', height: '100%', width: '100%', backgroundColor: '#0f172a', overflow: 'hidden' },
  // v0.5.107: Professional Dark Top Nav
  header: { 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'space-between', 
    padding: '12px 16px', 
    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
    borderBottom: '1px solid #334155', 
    gap: '16px' 
  },
  headerLeft: { display: 'flex', gap: '8px' },
  headerCenter: { flex: 1, maxWidth: '300px' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  viewButton: { 
    padding: '10px 16px', 
    borderRadius: '8px', 
    border: '1px solid #475569', 
    fontSize: '14px', 
    fontWeight: '600', 
    cursor: 'pointer',
    backgroundColor: '#1e293b',
    color: '#e2e8f0',
    transition: 'all 0.2s ease'
  },
  playNameInput: { width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #475569', fontSize: '16px', fontWeight: '600', textAlign: 'center', backgroundColor: '#0f172a', color: '#f8fafc' },
  saveButton: { padding: '10px 18px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 12px rgba(16,185,129,0.3)' },
  helpToggle: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#94a3b8', cursor: 'pointer', padding: '8px 12px', borderRadius: '8px', backgroundColor: '#1e293b', border: '1px solid #475569', fontWeight: '500' },
  content: { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', width: '100%' },
  designerLayout: { display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' },
  toolbar: { display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 16px', backgroundColor: '#fff', borderBottom: '1px solid #e5e7eb', overflowX: 'auto', flexShrink: 0 },
  drawingToolbar: { 
    display: 'flex', 
    alignItems: 'center', 
    gap: '10px', 
    padding: '12px 16px', 
    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
    borderBottom: '1px solid rgba(148, 163, 184, 0.1)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    overflowX: 'auto', 
    flexShrink: 0,
    WebkitOverflowScrolling: 'touch'  // Smooth scrolling on iOS
  },
  toolbarGroup: { display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 },
  toolbarDivider: { width: '1px', height: '28px', backgroundColor: 'rgba(148, 163, 184, 0.2)', flexShrink: 0 },
  toolbarLabel: { fontSize: '13px', color: '#94a3b8', fontWeight: '600', letterSpacing: '0.025em' },
  modeButton: { padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  toolButton: { padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '13px', cursor: 'pointer', backgroundColor: '#f3f4f6' },
  actionButton: { 
    padding: '10px 16px', 
    borderRadius: '8px', 
    border: 'none', 
    fontSize: '14px', 
    cursor: 'pointer', 
    fontWeight: '600', 
    display: 'flex', 
    alignItems: 'center', 
    gap: '6px', 
    whiteSpace: 'nowrap',
    background: 'linear-gradient(135deg, #334155 0%, #1e293b 100%)',
    color: '#e2e8f0',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)',
    transition: 'all 0.15s ease',
    minHeight: '44px'  // iOS recommended touch target
  },
  drawButton: { padding: '6px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '12px', cursor: 'pointer', fontWeight: '500' },
  clearButton: { padding: '4px 8px', borderRadius: '4px', border: 'none', fontSize: '11px', cursor: 'pointer', backgroundColor: '#fee2e2', color: '#991b1b' },
  clearButtonDark: { 
    padding: '6px 10px', 
    borderRadius: '6px', 
    border: 'none', 
    fontSize: '12px', 
    cursor: 'pointer', 
    background: 'rgba(239, 68, 68, 0.2)', 
    color: '#fca5a5',
    fontWeight: '500'
  },
  cancelButton: { padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '12px', cursor: 'pointer', backgroundColor: '#fee2e2', color: '#991b1b', fontWeight: '500', marginLeft: 'auto' },
  cancelButtonDark: { 
    padding: '8px 14px', 
    borderRadius: '6px', 
    border: 'none', 
    fontSize: '13px', 
    cursor: 'pointer', 
    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)', 
    color: '#fff', 
    fontWeight: '600', 
    marginLeft: 'auto',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
  },
  select: { padding: '6px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', outline: 'none' },
  selectSmall: { padding: '4px 8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '12px', outline: 'none' },
  checkboxLabel: { display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', cursor: 'pointer' },
  designerArea: { flex: 1, display: 'flex', overflow: 'hidden', padding: '8px', gap: '8px', boxSizing: 'border-box', width: '100%' },
  courtSection: { flex: '1 1 0%', display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0, overflow: 'auto', position: 'relative' },
  courtWrapper: { position: 'relative', flexShrink: 0, minHeight: '400px', maxHeight: '600px', backgroundColor: '#e5e7eb', borderRadius: '8px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '1 1 auto' },
  drawingIndicator: { position: 'absolute', top: '8px', left: '50%', transform: 'translateX(-50%)', padding: '6px 12px', backgroundColor: 'rgba(59, 130, 246, 0.9)', color: '#fff', borderRadius: '6px', fontSize: '13px', fontWeight: '500', zIndex: 10 },
  animationControls: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', flexWrap: 'wrap', flexShrink: 0 },
  controlButton: { width: '44px', height: '44px', borderRadius: '8px', border: 'none', backgroundColor: '#f3f4f6', fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  animStepIndicator: { display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', backgroundColor: '#f3f4f6', borderRadius: '6px' },
  animStepLabel: { fontSize: '11px', color: '#6b7280' },
  animStepNum: { fontSize: '16px', fontWeight: '700', color: '#3b82f6', minWidth: '20px', textAlign: 'center' },
  speedControl: { display: 'flex', alignItems: 'center', gap: '4px', marginLeft: 'auto' },
  speedLabel: { fontSize: '11px', color: '#6b7280' },
  speedSlider: { width: '50px', cursor: 'pointer', accentColor: '#3b82f6' },
  speedValue: { fontSize: '12px', fontWeight: '600', color: '#374151', minWidth: '16px' },
  speedButton: { width: '24px', height: '24px', borderRadius: '4px', border: '1px solid #d1d5db', backgroundColor: '#fff', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  sidePanel: { width: '280px', display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0, flexGrow: 0, height: 'calc(100vh - 180px)', minHeight: '400px', transition: 'width 0.2s ease, opacity 0.2s ease' },
  sidePanelCollapsed: { width: '0px', overflow: 'hidden', opacity: 0, padding: 0 },
  
  // v0.5.138: Tablet Floating Action Overlay
  floatingActionOverlay: {
    position: 'absolute',
    left: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '12px 10px',
    background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
    zIndex: 20,
    backdropFilter: 'blur(8px)',
    minWidth: '100px'
  },
  floatingActionHeader: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#94a3b8',
    textAlign: 'center',
    paddingBottom: '6px',
    borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
    marginBottom: '2px'
  },
  floatingActionBtn: {
    padding: '12px 14px',
    borderRadius: '8px',
    border: 'none',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'linear-gradient(135deg, #334155 0%, #1e293b 100%)',
    color: '#e2e8f0',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    transition: 'all 0.15s ease',
    minHeight: '44px',
    justifyContent: 'center'
  },
  floatingActionBtnActive: {
    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    color: '#fff'
  },
  floatingActionBtnActiveGreen: {
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    color: '#fff'
  },
  floatingActionBtnActiveBlue: {
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: '#fff'
  },
  floatingActionBtnActiveRed: {
    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    color: '#fff'
  },
  floatingActionBtnActivePurple: {
    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    color: '#fff'
  },
  floatingActionDivider: {
    height: '1px',
    backgroundColor: 'rgba(148, 163, 184, 0.2)',
    margin: '4px 0'
  },
  floatingHint: {
    position: 'absolute',
    left: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    padding: '12px 16px',
    background: 'rgba(30, 41, 59, 0.9)',
    borderRadius: '8px',
    color: '#94a3b8',
    fontSize: '14px',
    fontWeight: '500',
    zIndex: 20,
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
  },
  sidePanelToggle: { position: 'fixed', top: '50%', transform: 'translateY(-50%)', zIndex: 100, padding: '12px 8px', borderRadius: '8px 0 0 8px', border: 'none', backgroundColor: '#3b82f6', color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '-2px 0 8px rgba(0,0,0,0.3)', transition: 'right 0.2s ease', writingMode: 'vertical-rl', textOrientation: 'mixed' },
  statusPanel: { padding: '12px', backgroundColor: '#0f172a', borderRadius: '8px', marginTop: '8px' },
  statusText: { margin: '4px 0', fontSize: '11px', color: '#94a3b8' },
  versionBadge: { fontSize: '10px', color: '#9ca3af', textAlign: 'center', padding: '4px', borderTop: '1px solid #374151', marginTop: 'auto' },
  
  // Screen panel styles
  screenPanel: { padding: '10px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  screenPanelTitle: { fontSize: '12px', fontWeight: '600', color: '#374151', margin: '0 0 8px 0' },
  screenItem: { display: 'flex', flexDirection: 'column', gap: '6px', padding: '8px', backgroundColor: '#f9fafb', borderRadius: '6px', marginBottom: '8px', position: 'relative' },
  screenLabel: { fontSize: '12px', fontWeight: '500', color: '#374151' },
  screenControls: { display: 'flex', flexDirection: 'column', gap: '6px' },
  screenSlider: { display: 'flex', alignItems: 'center', gap: '8px' },
  screenSliderLabel: { fontSize: '11px', color: '#6b7280', width: '40px', flexShrink: 0 },
  miniSlider: { flex: 1, minWidth: '60px', height: '4px', cursor: 'pointer', accentColor: '#f59e0b' },
  screenSliderValue: { fontSize: '11px', color: '#374151', width: '35px', textAlign: 'right', fontFamily: 'monospace', flexShrink: 0 },
  deleteScreenBtn: { position: 'absolute', top: '6px', right: '6px', width: '18px', height: '18px', border: 'none', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  
  // Action Log panel styles
  actionLogPanel: { padding: '10px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', maxHeight: '200px', display: 'flex', flexDirection: 'column' },
  actionLogTitle: { fontSize: '12px', fontWeight: '600', color: '#374151', margin: '0 0 8px 0' },
  actionLogList: { flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '3px' },
  actionLogEmpty: { fontSize: '11px', color: '#9ca3af', textAlign: 'center', padding: '12px 4px', fontStyle: 'italic' },
  actionLogItem: { display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 8px', backgroundColor: '#f9fafb', borderRadius: '4px', borderLeft: '3px solid #3b82f6', transition: 'background-color 0.15s' },
  actionLogStep: { width: '16px', height: '16px', borderRadius: '50%', color: '#fff', fontSize: '9px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  actionLogText: { flex: 1, fontSize: '11px', color: '#374151', fontWeight: '500' },
  actionStepControls: { display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 },
  stepAdjustBtn: { width: '14px', height: '14px', border: 'none', backgroundColor: '#e5e7eb', borderRadius: '2px', cursor: 'pointer', fontSize: '10px', fontWeight: 'bold', color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, lineHeight: 1 },
  simultaneousBadge: { fontSize: '9px', backgroundColor: '#fef3c7', color: '#92400e', padding: '1px 4px', borderRadius: '8px', fontWeight: '600', marginLeft: 'auto' },
  
  // v0.5.45: Step-specific notes styles
  stepNotesSection: { padding: '8px 12px', backgroundColor: '#f0f9ff', borderRadius: '8px', margin: '0 12px 8px 12px', border: '1px solid #bae6fd' },
  stepNotesHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' },
  stepNotesLabel: { fontSize: '13px', fontWeight: '600', color: '#0369a1' },
  stepNotesClear: { width: '20px', height: '20px', border: 'none', backgroundColor: 'transparent', color: '#64748b', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' },
  stepNotesInput: { width: '100%', height: '50px', padding: '8px', borderRadius: '6px', border: '1px solid #bae6fd', fontSize: '13px', resize: 'none', boxSizing: 'border-box', backgroundColor: '#fff' },
  stepZeroInfo: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', backgroundColor: '#f8fafc', borderRadius: '8px', margin: '0 12px 8px 12px', border: '1px solid #e2e8f0', fontSize: '13px', color: '#475569' },
  
  // Legacy notes section (replaced by step notes)
  notesSection: { padding: '0 12px 12px 12px', flexShrink: 0, display: 'none' },
  notesInput: { width: '100%', height: '60px', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '13px', resize: 'none', boxSizing: 'border-box' },
  
  // Steps panel styles
  stepsSection: { 
    padding: '16px', 
    margin: '0 12px 12px 12px',
    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
    borderRadius: '12px',
    border: '1px solid rgba(148, 163, 184, 0.1)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    flexShrink: 0 
  },
  stepsHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px solid rgba(148, 163, 184, 0.1)' },
  stepsTitle: { fontSize: '14px', fontWeight: '600', color: '#e2e8f0', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' },
  stepsCount: { fontSize: '12px', color: '#64748b', backgroundColor: 'rgba(100, 116, 139, 0.2)', padding: '4px 10px', borderRadius: '12px', fontWeight: '500' },
  stepsList: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px', maxHeight: '200px', overflowY: 'auto' },
  stepItem: { 
    display: 'flex', 
    alignItems: 'center', 
    gap: '10px', 
    padding: '10px 12px', 
    background: 'rgba(51, 65, 85, 0.5)',
    borderRadius: '8px', 
    border: '1px solid rgba(148, 163, 184, 0.1)',
    transition: 'all 0.15s ease'
  },
  stepNumber: { 
    width: '28px', 
    height: '28px', 
    borderRadius: '50%', 
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', 
    color: '#fff', 
    fontSize: '13px', 
    fontWeight: '700', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    flexShrink: 0,
    boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
  },
  stepText: { flex: 1, fontSize: '13px', color: '#e2e8f0', cursor: 'pointer', padding: '4px 0' },
  stepEditInput: { flex: 1, fontSize: '13px', padding: '6px 10px', border: '1px solid #3b82f6', borderRadius: '6px', outline: 'none', backgroundColor: '#1e293b', color: '#e2e8f0' },
  stepActions: { display: 'flex', gap: '4px', flexShrink: 0 },
  stepActionBtn: { 
    width: '26px', 
    height: '26px', 
    border: 'none', 
    background: 'rgba(71, 85, 105, 0.5)', 
    borderRadius: '6px', 
    cursor: 'pointer', 
    fontSize: '12px', 
    color: '#94a3b8', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    transition: 'all 0.15s ease'
  },
  addStepRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  addStepInput: { 
    flex: 1, 
    padding: '10px 14px', 
    borderRadius: '8px', 
    border: '1px solid rgba(148, 163, 184, 0.2)', 
    fontSize: '13px', 
    outline: 'none',
    background: 'rgba(30, 41, 59, 0.8)',
    color: '#e2e8f0'
  },
  addStepBtn: { 
    padding: '10px 16px', 
    borderRadius: '8px', 
    border: 'none', 
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
    color: '#fff', 
    fontSize: '13px', 
    fontWeight: '600', 
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
  },
  
  emptyState: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px', gap: '16px' },
  emptyIcon: { fontSize: '64px' },
  emptyText: { fontSize: '16px', color: '#6b7280' },
  emptyButton: { padding: '12px 24px', borderRadius: '8px', border: 'none', backgroundColor: '#3b82f6', color: '#fff', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  
  // v0.5.18: Enhanced Timeline scrubber styles
  timelineContainer: { 
    padding: '16px 20px', 
    background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
    borderRadius: '12px',
    border: '1px solid rgba(148, 163, 184, 0.1)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)'
  },
  timelineTrack: {
    position: 'relative',
    height: '40px',
    background: 'linear-gradient(180deg, rgba(51, 65, 85, 0.6) 0%, rgba(30, 41, 59, 0.8) 100%)',
    borderRadius: '8px',
    cursor: 'pointer',
    overflow: 'visible',
    border: '1px solid rgba(148, 163, 184, 0.15)',
    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)'
  },
  timelineProgress: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    background: 'linear-gradient(90deg, rgba(59, 130, 246, 0.4) 0%, rgba(59, 130, 246, 0.2) 100%)',
    borderRadius: '8px 0 0 8px',
    pointerEvents: 'none'
  },
  timelineMarker: {
    position: 'absolute',
    top: '50%',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    transform: 'translateX(-50%) translateY(-50%)',
    border: '2px solid #fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    zIndex: 2,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
  },
  timelineMarkerLabel: {
    fontSize: '9px',
    fontWeight: 'bold',
    color: '#fff'
  },
  timelinePlayhead: {
    position: 'absolute',
    top: '-6px',
    width: '6px',
    height: '52px',
    background: 'linear-gradient(180deg, #f97316 0%, #ea580c 100%)',
    borderRadius: '3px',
    transform: 'translateX(-50%)',
    boxShadow: '0 0 12px rgba(249, 115, 22, 0.6), 0 0 24px rgba(249, 115, 22, 0.3)',
    pointerEvents: 'none',
    zIndex: 3
  },
  timelineHint: {
    marginTop: '8px',
    fontSize: '11px',
    color: '#64748b',
    textAlign: 'center'
  }
};

export default PlayDesigner;
